import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { notificar } from '../utils/notify';
import AuditoriaFolha from '../components/AuditoriaFolha';
import '../styles/bporh_admissoes.css';

// Folha de Pagamento — as variáveis do mês que vão para o DP.
//
// Duas telas no mesmo componente, como no Ponto: a lista de empresas
// (/Dp/Folha de Pagamento) e a folha de uma empresa
// (/BpoRh/Folha/cliente/:clienteId), que é a planilha "Variáveis de folha"
// virada tela: uma linha por colaborador, uma coluna por RUBRICA do cliente.
// O botão de download gera o arquivo de importação do Domínio: uma linha de 43
// dígitos por rubrica preenchida (matrícula, competência, rubrica, tipo de
// folha, valor em centavos e o código da empresa no Domínio).

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const norm = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
// número -> texto brasileiro do input (o DP trabalha com vírgula decimal)
const paraBr = (v) => (v == null || v === '' ? ''
  : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const cpfFmt = (v) => (v && v.length === 11
  ? `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}` : (v || '—'));

// Colunas de cadastro da tabela: cada cliente usa as da planilha dele (a Setho
// abre com MAT e SALÁRIO, a BBDA com CPF) — quem manda é a configuração.
const valorDaColuna = (c, coluna) => {
  if (coluna === 'cpf') return cpfFmt(c.cpf);
  if (coluna === 'salario') return c.salario != null ? `R$ ${paraBr(c.salario)}` : '—';
  if (coluna === 'admissao') return c.admissao || '—';
  return c[coluna] || '—';
};

// Célula da matrícula: é SEMPRE a do Domínio — a da Convenia não vale para o
// arquivo (os números divergem). Sem ela, a célula fica vermelha e a pessoa não
// entra no arquivo do DP; a da Convenia aparece como sugestão de um clique.
const CelulaMatricula = ({ col, onSalvar }) => {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(col.matricula || '');
  useEffect(() => { setTexto(col.matricula || ''); }, [col.matricula]);
  if (editando) {
    return (
      <input type="text" inputMode="numeric" autoFocus className="form-control form-control-sm"
        style={{ width: 80 }} value={texto}
        onChange={(e) => setTexto(e.target.value.replace(/\D/g, ''))}
        onBlur={() => { setEditando(false); if (texto !== (col.matricula || '')) onSalvar(texto); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') { setTexto(col.matricula || ''); setEditando(false); }
        }} />
    );
  }
  if (!col.matricula) {
    return (
      <div className="d-flex align-items-center gap-1">
        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none fw-bold"
          onClick={() => setEditando(true)}
          style={{ color: '#a11d2b', borderBottom: '1px dotted #dc3545' }}
          title="Sem matrícula do Domínio: esta pessoa NÃO entra no arquivo do DP. Clique para informar.">
          <i className="fas fa-triangle-exclamation me-1"></i>sem matrícula
        </button>
        {col.matricula_convenia && (
          <button type="button" className="btn btn-sm btn-light border py-0 px-1"
            onClick={() => onSalvar(col.matricula_convenia)}
            title={`Usar ${col.matricula_convenia}, que é a matrícula da Convenia — confirme se no Domínio é a mesma`}
            style={{ fontSize: '.7rem' }}>
            usar {col.matricula_convenia}
          </button>
        )}
      </div>
    );
  }
  return (
    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none"
      onClick={() => setEditando(true)}
      style={{ color: '#212529', borderBottom: '1px dotted #adb5bd',
        fontVariantNumeric: 'tabular-nums' }}
      title={`Matrícula no Domínio${col.matricula_convenia
        ? ` (na Convenia é ${col.matricula_convenia})` : ''} — clique para alterar`}>
      {col.matricula}
      <i className="fas fa-pen ms-1" style={{ fontSize: '.55rem', opacity: .7 }}></i>
    </button>
  );
};

// A tabela cresce nos dois sentidos (uma coluna por rubrica, uma linha por
// colaborador). O NOME fica preso à esquerda e o CABEÇALHO ao topo; só o miolo
// rola. z-index: 3 no canto (nome + cabeçalho), 2 no cabeçalho, 1 no nome.
const NOME_FIXO = {
  position: 'sticky', left: 0, zIndex: 1, background: '#fff',
  borderRight: '1px solid #dee2e6',
};
// Duas coisas quebram o sticky das células e precisam ser desfeitas AQUI:
//  · 'collapse' do Bootstrap  → sem SEPARATE a coluna do nome rola junto;
//  · 'overflow: hidden' que o theme.css põe em toda .table (para o border-radius)
//    → recorta o conteúdo e o sticky para de valer. Inline vence os dois sem
//    mexer no tema global.
const TABELA_FIXA = {
  minWidth: 900, borderCollapse: 'separate', borderSpacing: 0,
  overflow: 'visible', marginTop: 0,
};
const CABECALHO_FIXO = { position: 'sticky', top: 0, zIndex: 2, background: '#f8f9fa' };
const CANTO_FIXO = {
  ...NOME_FIXO, ...CABECALHO_FIXO, zIndex: 3, background: '#f8f9fa', minWidth: 230,
};
// o rodapé NÃO é preso: a linha de totais fecha a tabela no fim da rolagem
const RODAPE = { background: '#f8f9fa' };

// Férias, afastamento, admissão e desligamento dentro da competência: o DP
// precisa ver isso na folha, porque muda VA, VT, horas e às vezes a rubrica.
const COR_ALERTA = {
  ferias: { bg: '#e3f2fd', cor: '#0a6ea1', borda: '#0a6ea1', icone: 'fa-umbrella-beach' },
  afastamento: { bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48', icone: 'fa-notes-medical' },
  novo: { bg: '#e8f5e9', cor: '#1b5e20', borda: '#2e7d32', icone: 'fa-user-plus' },
  desligado: { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-user-minus' },
};

const SeloAlerta = ({ a }) => {
  const c = COR_ALERTA[a.tipo] || COR_ALERTA.afastamento;
  return (
    <span className="badge me-1"
      style={{ background: c.bg, color: c.cor, border: `1px solid ${c.borda}`, fontWeight: 500 }}
      title={`${a.rotulo}: ${a.de}${a.ate ? ` a ${a.ate}` : ''}`
        + (a.dias_no_mes ? ` · ${a.dias_no_mes} dia(s) nesta competência` : '')}>
      <i className={`fas ${c.icone} me-1`}></i>
      {a.rotulo}
      {a.ate ? ` ${a.de.slice(0, 5)}–${a.ate.slice(0, 5)}` : ` ${a.de}`}
      {a.mes_inteiro ? ' · mês inteiro' : (a.dias_no_mes ? ` · ${a.dias_no_mes}d` : '')}
    </span>
  );
};

// Onde cada valor automático é pago/apurado — é para lá que o clique leva.
const ROTA_DA_FONTE = {
  va_desconto: (id, mes) => `/BpoRh/Beneficios/cliente/${id}?mes=${mes}`,
  va_total: (id, mes) => `/BpoRh/Beneficios/cliente/${id}?mes=${mes}`,
  va_pago: (id, mes) => `/BpoRh/Beneficios/cliente/${id}?mes=${mes}`,
  vt_total: (id, mes) => `/BpoRh/ValeTransporte/cliente/${id}?mes=${mes}`,
  ponto_extras: (id, mes) => `/BpoRh/FechamentoPonto?cliente=${id}&mes=${mes}`,
  ponto_faltas: (id, mes) => `/BpoRh/FechamentoPonto?cliente=${id}&mes=${mes}`,
  ponto_noturno: (id, mes) => `/BpoRh/FechamentoPonto?cliente=${id}&mes=${mes}`,
  adiantamento: (id, mes) => `/BpoRh/Adiantamento?cliente=${id}&mes=${mes}`,
};
const rotaDaFonte = (fonte, clienteId, mes) => (
  fonte && ROTA_DA_FONTE[fonte] ? ROTA_DA_FONTE[fonte](clienteId, mes) : null);

// Célula da folha.
//
// Rubrica com FONTE (VA, VT, ponto): o valor é um botão — clicar abre a memória
// de cálculo (a sub-tabela que hoje vive na planilha). O lápis ao lado libera a
// digitação, e o que for digitado vence a fonte.
// Rubrica manual: campo de digitação direto.
const Celula = ({ valor, sugestao, unidade, fonte, fonteRotulo, onSalvar, onDetalhe, bloqueada }) => {
  const doModulo = valor == null && sugestao != null;
  const efetivo = valor != null ? valor : sugestao;
  const [texto, setTexto] = useState(paraBr(efetivo));
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState(false);
  useEffect(() => { setTexto(paraBr(efetivo)); }, [efetivo]);
  const confirmar = () => {
    setEditando(false);
    if (texto === paraBr(efetivo)) return;
    setSalvando(true);
    Promise.resolve(onSalvar(texto)).finally(() => setSalvando(false));
  };
  const dica = unidade === 'horas' ? 'Horas em decimal (5,92 = 5h55min)' : 'Valor em R$ (210,00)';

  // folha fechada: só leitura (o cálculo ainda abre, para conferência)
  if (bloqueada) {
    const estilo = { fontVariantNumeric: 'tabular-nums', color: doModulo ? '#0a6ea1' : '#212529',
      fontStyle: doModulo ? 'italic' : 'normal' };
    return (
      <div className="text-end pe-1" title="Folha fechada — reabra para alterar">
        {fonte && efetivo != null ? (
          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={onDetalhe} style={{ ...estilo, borderBottom: '1px dotted #0a6ea1' }}>
            {paraBr(efetivo)}
          </button>
        ) : <span style={estilo}>{paraBr(efetivo) || <span className="text-muted">—</span>}</span>}
      </div>
    );
  }

  if (fonte && !editando) {
    return (
      <div className="d-flex align-items-center justify-content-end gap-1">
        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none"
          onClick={onDetalhe}
          title={`${fonteRotulo || 'Valor automático'} — clique para ver o cálculo`}
          style={{ fontVariantNumeric: 'tabular-nums',
            color: doModulo ? '#0a6ea1' : '#212529',
            fontStyle: doModulo ? 'italic' : 'normal',
            borderBottom: '1px dotted #0a6ea1' }}>
          {paraBr(efetivo) || <span className="text-muted">—</span>}
        </button>
        <button type="button" className="btn btn-sm btn-light border py-0 px-1"
          onClick={() => setEditando(true)} disabled={salvando}
          title={doModulo ? 'Lançar um valor à mão (substitui o automático)' : 'Alterar o lançamento'}>
          <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-pen'} text-muted`}
            style={{ fontSize: '.65rem' }}></i>
        </button>
      </div>
    );
  }
  return (
    <input type="text" inputMode="decimal" autoFocus={editando}
      className={`form-control form-control-sm text-end ${salvando ? 'bg-light' : ''}`}
      style={{ minWidth: 84, fontVariantNumeric: 'tabular-nums' }}
      title={dica} value={texto} disabled={salvando}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { setTexto(paraBr(efetivo)); setEditando(false); }
      }} />
  );
};

// Um campo de arquivo: diz o que se espera, o que já foi importado no mês e,
// depois da leitura, a conferência antes de gravar.
const BlocoImportacao = ({ chave, titulo, icone, dica, regra, rubrica, jaImportado,
  imp, onEscolher, onGravar, dadosFolha, extraCabecalho }) => {
  const previa = (imp.previas || {})[chave];
  const ocupado = imp.ocupado === chave;
  return (
    <div className="border rounded p-3 mb-2">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div style={{ minWidth: 260, flex: 1 }}>
          <div className="fw-bold">
            <i className={`fas ${icone || 'fa-file-lines'} me-2`} style={{ color: '#0a6ea1' }}></i>
            {titulo}
            {rubrica && (
              <span className="badge bg-light text-dark border ms-2 fw-normal">
                → {rubrica.rotulo}{rubrica.codigo ? ` (${rubrica.codigo})` : ''}
              </span>
            )}
            {rubrica === undefined && (
              <span className="badge bg-warning text-dark ms-2 fw-normal">
                sem rubrica nesta empresa
              </span>
            )}
          </div>
          <div className="small text-muted mt-1">{dica}</div>
          <div className="small text-muted">
            <i className="fas fa-wand-magic-sparkles me-1" style={{ color: '#7e57c2' }}></i>
            Outro formato (PDF, planilha do fornecedor)? Mande assim mesmo: a IA lê e mostra
            o que entendeu antes de você importar.
          </div>
          {regra && <div className="small text-muted"><i className="fas fa-calculator me-1"></i>{regra}</div>}
          {jaImportado && (
            <div className="small mt-1" style={{ color: '#1b5e20' }}>
              <i className="fas fa-circle-check me-1"></i>
              {jaImportado.linhas} linha(s) de <b>{jaImportado.arquivo}</b>
              {jaImportado.aba ? ` · aba ${jaImportado.aba}` : ''} · {jaImportado.em}
              {jaImportado.autor ? ` por ${jaImportado.autor}` : ''}
              {' · '}R$ {paraBr(jaImportado.total)}
            </div>
          )}
        </div>
        <label className="btn btn-sm btn-outline-primary mb-0"
          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-folder-open'} me-2`}></i>
          {jaImportado ? 'Trocar arquivo' : 'Escolher arquivo'}
          <input type="file" hidden accept=".csv,.txt,.xlsx,.xlsm,.pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => { onEscolher(e.target.files[0]); e.target.value = ''; }} />
        </label>
      </div>

      {extraCabecalho}

      {previa && !previa.success && (
        <div className="alert alert-danger small py-2 mt-2 mb-0">{previa.mensagem}</div>
      )}

      {/* arquivo com várias abas: o operador diz qual é a planilha certa */}
      {previa?.success && (previa.escolher_aba || []).length > 0 && (
        <div className="alert alert-light border small py-2 mt-2 mb-0">
          <div className="fw-bold mb-1">
            <i className="fas fa-layer-group me-1 text-muted"></i>{previa.mensagem}
          </div>
          <div className="d-flex flex-wrap gap-1">
            {previa.escolher_aba.map((a) => (
              <button key={a.nome} className="btn btn-sm btn-outline-primary py-0"
                disabled={ocupado}
                onClick={() => onEscolher(previa.file, a.nome)}
                title={`${a.linhas} linha(s) · ${(a.colunas || []).join(' · ')}`}>
                {a.nome} <span className="text-muted">({a.linhas})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {previa?.success && (
        <div className="mt-3">
          <div className="small text-muted mb-2">
            <i className="fas fa-file-lines me-1"></i>{previa.file?.name}
          </div>
          {previa.aba && (
            <div className="small text-muted mb-2">
              <i className="fas fa-layer-group me-1"></i>aba <b>{previa.aba}</b>
              <button className="btn btn-sm btn-link py-0" onClick={() => onEscolher(previa.file)}>
                trocar
              </button>
            </div>
          )}
          {(previa.blocos || []).map((b) => (
            <div key={b.tipo} className="border rounded p-2 mb-2" style={{ background: '#fff' }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div className="small">
                  <b>{b.rotulo}</b>
                  {b.pela_ia && (
                    <span className="badge ms-2" style={{ background: '#ede7f6', color: '#4527a0', border: '1px solid #7e57c2' }}
                      title="O layout deste arquivo não é conhecido: a IA leu e montou as linhas. Confira antes de importar.">
                      <i className="fas fa-wand-magic-sparkles me-1"></i>lido pela IA
                    </span>
                  )}
                  <span className="text-muted"> · {b.aba} · {b.linhas} linha(s) · </span>
                  <b>{b.pessoas.length}</b> colaborador(es) desta empresa
                </div>
                <div className="fw-bold" style={{ color: '#00205b' }}>R$ {paraBr(b.total)}</div>
              </div>

              {!b.competencia_confere && b.competencia_arquivo && (
                <div className="alert alert-warning small py-2">
                  <i className="fas fa-triangle-exclamation me-2"></i>
                  Arquivo da competência <b>{b.competencia_arquivo}</b>, folha aberta em
                  {' '}<b>{dadosFolha?.mes}</b>. Confira antes de gravar.
                </div>
              )}

              {b.pessoas.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: 240 }}>
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Colaborador</th>
                        <th style={{ width: 80 }}>MAT</th>
                        <th>Composição</th>
                        <th className="text-end" style={{ width: 110 }}>Desconto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.pessoas.map((pe) => (
                        <tr key={pe.convenia_id}>
                          <td>{pe.nome}</td>
                          <td className="text-muted small">{pe.matricula || '—'}</td>
                          <td className="small text-muted">
                            {pe.itens.map((it, i) => (
                              <div key={i}>
                                {it.nome}
                                {it.percentual && it.percentual !== 100
                                  ? ` · ${it.percentual}% de R$ ${paraBr(it.valor_original)}` : ''}
                                {' — R$ '}{paraBr(it.valor)}
                              </div>
                            ))}
                          </td>
                          <td className="text-end fw-bold">R$ {paraBr(pe.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* alerta nos dois sentidos (30/09/2026): quem sobrou no arquivo e quem
                  da folha não veio nele */}
              {b.sem_cadastro.length > 0 && (
                <div className="alert alert-danger small py-2 mt-2 mb-0">
                  <div className="fw-bold">
                    <i className="fas fa-user-xmark me-1"></i>
                    {b.sem_cadastro.length} pessoa(s) do arquivo não encontrada(s) nesta folha
                    {' '}— R$ {paraBr(b.total_sem_cadastro)} que NÃO serão lançados
                  </div>
                  <ul className="mb-0 mt-1 ps-3">
                    {b.sem_cadastro.map((pe) => (
                      <li key={`${pe.nome}-${pe.cpf_titular || ''}`}>
                        <b>{pe.nome}</b> · R$ {paraBr(pe.valor)}
                        {pe.motivo && <span className="text-muted"> — {pe.motivo}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(b.fora_do_arquivo || []).length > 0 && (
                <details className="alert alert-warning small py-2 mt-2 mb-0">
                  <summary className="fw-bold" style={{ cursor: 'pointer' }}>
                    <i className="fas fa-user-clock me-1"></i>
                    {b.fora_do_arquivo.length} colaborador(es) da folha não vieram neste arquivo
                    <span className="fw-normal text-muted"> — confira se deviam estar (clique para ver)</span>
                  </summary>
                  <div className="mt-1">
                    {b.fora_do_arquivo.map((nome) => (
                      <span key={nome} className="badge bg-light text-dark border me-1 mb-1">{nome}</span>
                    ))}
                  </div>
                </details>
              )}

              {(b.ignorados || []).length > 0 && (
                <div className={`alert ${b.pela_ia ? 'alert-light border' : 'alert-warning'} small py-2 mt-2 mb-0`}>
                  {b.pela_ia && (
                    <div className="fw-bold mb-1">
                      <i className="fas fa-wand-magic-sparkles me-1" style={{ color: '#4527a0' }}></i>
                      O que a IA identificou neste arquivo:
                    </div>
                  )}
                  {b.ignorados.map((x) => <div key={x}>· {x}</div>)}
                </div>
              )}
            </div>
          ))}
          <button className="btn btn-success btn-sm" onClick={onGravar} disabled={ocupado}>
            <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>
            Importar {(previa.blocos || []).length > 1 ? 'os arquivos conferidos' : 'este arquivo'}
          </button>
        </div>
      )}
    </div>
  );
};

const BpoRhFolha = () => {
  const { clienteId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const mes = searchParams.get('mes') || mesAtual();
  const setMes = (v) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set('mes', v); else n.delete('mes');
    return n;
  });
  // cliente com mais de uma empresa no Domínio (GaussFleet): uma folha por filial,
  // e a filial aberta fica na URL para o F5 e o link voltarem no mesmo lugar
  const filial = searchParams.get('filial') || '';
  const setFilial = (v) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set('filial', String(v)); else n.delete('filial');
    return n;
  });
  const [novaFilial, setNovaFilial] = useState('');
  // pessoa nova cadastrada à mão (cliente sem Convenia — prestadores de RPA)
  const PESSOA_VAZIA = { nome: '', cpf: '', matricula: '', vinculo: 'Autônomo', salario: '' };
  const [novaPessoa, setNovaPessoa] = useState(PESSOA_VAZIA);
  const editarPessoa = (idx, campo, valor) => setRubCfg((s) => ({
    ...s, pessoas: (s.pessoas || []).map((p, i) => (i === idx ? { ...p, [campo]: valor, _mudou: true } : p)),
  }));

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [soPreenchidos, setSoPreenchidos] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [limpando, setLimpando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { if (mensagem) notificar(mensagem); }, [mensagem]);
  // memória de cálculo de uma célula (a "sub-tabela" da planilha)
  const [detalhe, setDetalhe] = useState(null);   // {carregando, col, rubrica, dados}
  // configuração do arquivo do Domínio (empresa e tipo de folha), quando aberta
  const [cfg, setCfg] = useState(null);           // {empresa, tipo, colunas}
  const [salvandoCfg, setSalvandoCfg] = useState(false);
  // rubricas (colunas) da folha dentro das configurações
  const [rubCfg, setRubCfg] = useState(null);     // {rubricas, fontes, unidades, modelos, carregando, ocupado}
  // importação dos arquivos de benefício (plano de saúde, odonto, farmácia...)
  const [imp, setImp] = useState(null);           // {previas: {chave: previa}, ocupado}
  const [rubricaAlvo, setRubricaAlvo] = useState(''); // coluna de destino da planilha avulsa
  // coluna que o operador quer zerar no mês (confirmação antes)
  const [zerar, setZerar] = useState(null);       // {rubrica}
  const [zerando, setZerando] = useState(false);
  // fechamento do mês: a foto do que foi entregue ao DP
  const [salvando, setSalvando] = useState(false);
  const [resumo7, setResumo7] = useState(null);   // {lista, foto, carregando}

  const abrirDetalhe = (col, rubrica) => {
    setDetalhe({ carregando: true, col, rubrica, dados: null });
    axios.get(`${API}/api/bporh/folha/detalhe/`, {
      params: { cliente_id: clienteId, filial: filial || undefined, mes, convenia_id: col.convenia_id, rubrica_id: rubrica.id },
      headers: headers(),
    })
      .then((res) => setDetalhe({ carregando: false, col, rubrica, dados: res.data }))
      .catch((err) => {
        setDetalhe(null);
        setMensagem(`❌ ${err.response?.data?.erro || 'Não foi possível abrir o cálculo'}`);
      });
  };

  // A tela só mostra a tabela quando está TUDO pronto — inclusive os valores
  // que vêm do VA, do VT e do ponto, que levam alguns segundos. Até lá, só o
  // ícone de carregando (meia tabela dá a impressão de dado faltando).
  const carregar = useCallback(() => {
    setCarregando(true);
    setDados(null);
    axios.get(`${API}/api/bporh/folha/`, {
      // '_' com o instante da chamada: a folha sempre lê VA, VT e ponto de novo
      // ao abrir (nada de resposta velha do cache do navegador)
      params: clienteId ? { cliente_id: clienteId, filial: filial || undefined, mes, _: Date.now() } : { _: Date.now() },
      headers: { ...headers(), 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then((res) => setDados(res.data))
      .catch((err) => {
        setDados(null);
        setMensagem(`❌ ${err.response?.data?.erro || 'Não foi possível carregar a folha'}`);
      })
      .finally(() => setCarregando(false));
  }, [clienteId, mes, filial]);
  useEffect(() => { carregar(); }, [carregar]);

  const clientes = useMemo(() => dados?.clientes || [], [dados]);
  const rubricas = useMemo(() => dados?.rubricas || [], [dados]);
  const colaboradores = useMemo(() => dados?.colaboradores || [], [dados]);
  const atual = clientes.find((c) => String(c.id) === String(clienteId));

  // empresas escondidas da lista (não têm folha por aqui) só aparecem se pedir
  const [verOcultas, setVerOcultas] = useState(false);
  const clientesFiltrados = useMemo(() => clientes.filter(
    (c) => (verOcultas || !c.oculta)
      && (!busca || norm(`${c.cliente} ${c.operador || ''}`).includes(norm(busca)))), [clientes, busca, verOcultas]);
  const ocultas = useMemo(() => clientes.filter((c) => c.oculta).length, [clientes]);
  const ocultarEmpresa = (c, oculta) => axios.post(`${API}/api/bporh/folha/valor/`,
    { cliente_id: c.id, acao: 'ocultar', oculta }, { headers: headers() })
    .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); carregar(); })
    .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível alterar'}`));

  // efetivo = digitado, senão o que veio do módulo (é o que vai no arquivo)
  const efetivoDe = (c, r) => {
    const manual = (c.valores || {})[r.id];
    return manual != null ? manual : (c.fontes || {})[r.id];
  };

  const linhas = useMemo(() => colaboradores.filter((c) => {
    if (soPreenchidos && ![...Object.values(c.valores || {}), ...Object.values(c.fontes || {})].some((v) => v)) return false;
    if (busca && !norm(`${c.nome} ${c.matricula || ''} ${c.cargo || ''}`).includes(norm(busca))) return false;
    return true;
  }), [colaboradores, soPreenchidos, busca]);

  const totais = useMemo(() => {
    const t = {};
    rubricas.forEach((r) => {
      t[r.id] = linhas.reduce((s, c) => s + Number(efetivoDe(c, r) || 0), 0);
    });
    return t;
  }, [linhas, rubricas]);

  // grava a matrícula do Domínio de um colaborador (vazio volta para a da Convenia)
  const salvarMatricula = (col, texto) => axios.post(`${API}/api/bporh/folha/valor/`, {
    cliente_id: Number(clienteId), filial: filial || undefined, acao: 'matricula', convenia_id: col.convenia_id, matricula: texto,
  }, { headers: headers() })
    .then((res) => {
      setMensagem(`✅ ${res.data.mensagem}`);
      setDados((d) => (d ? { ...d,
        colaboradores: d.colaboradores.map((c) => (c.convenia_id === col.convenia_id
          ? { ...c, matricula: res.data.matricula || null,
            matricula_dominio: !!res.data.matricula } : c)) } : d));
    })
    .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar a matrícula'}`));

  // tira o colaborador da folha DESTE mês (ou o traz de volta) — não mexe no cadastro
  const mudarNaFolha = (col, sair) => axios.post(`${API}/api/bporh/folha/valor/`, {
    cliente_id: Number(clienteId), filial: filial || undefined, mes, convenia_id: col.convenia_id,
    acao: sair ? 'excluir_colaborador' : 'incluir_colaborador',
  }, { headers: headers() })
    .then((res) => { setMensagem(`✅ ${col.nome}: ${res.data.mensagem}`); carregar(); })
    .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível alterar'}`));

  // salva uma célula e atualiza só o colaborador mexido (a tabela é grande)
  const salvarValor = (col, rubrica, texto) => axios.post(`${API}/api/bporh/folha/valor/`, {
    cliente_id: Number(clienteId), filial: filial || undefined, mes, convenia_id: col.convenia_id,
    rubrica_id: rubrica.id, valor: texto,
  }, { headers: headers() })
    .then((res) => {
      setDados((d) => (d ? {
        ...d,
        colaboradores: d.colaboradores.map((c) => (c.convenia_id === col.convenia_id
          ? { ...c,
            valores: res.data.valor == null
              ? Object.fromEntries(Object.entries(c.valores || {}).filter(([k]) => Number(k) !== rubrica.id))
              : { ...(c.valores || {}), [rubrica.id]: res.data.valor } }
          : c)),
      } : d));
    })
    .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`));

  // desfaz o "Puxar": tira os valores congelados dos módulos e volta a ler as fontes
  const limparAutomaticos = () => {
    setLimpando(true);
    axios.post(`${API}/api/bporh/folha/valor/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, mes, acao: 'limpar_automaticos',
    }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível limpar'}`))
      .finally(() => setLimpando(false));
  };

  // ─── Configurações da folha: rubricas (colunas), suas fontes e parâmetros ───
  const abrirConfig = () => {
    setCfg({ empresa: arquivo.empresa_dominio || '', tipo: arquivo.tipo_folha || '11', colunas,
      vinculos: dados?.vinculos || Object.keys(dados?.vinculos_catalogo || {}),
      prazo_dia: dados?.prazo?.dia ? String(dados.prazo.dia) : '', prazo_mes_seguinte: !!dados?.prazo?.mes_seguinte });
    setRubCfg({ rubricas: [], fontes: [], unidades: {}, modelos: [], carregando: true, ocupado: null });
    axios.get(`${API}/api/bporh/folha/rubricas/`, { params: { cliente_id: clienteId }, headers: headers() })
      .then((r) => setRubCfg({ ...r.data, carregando: false, ocupado: null }))
      .catch((err) => {
        setRubCfg((s) => ({ ...(s || {}), carregando: false }));
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível carregar as rubricas'}`);
      });
  };

  const acaoRubrica = (corpo, chave) => {
    setRubCfg((s) => ({ ...s, ocupado: chave }));
    return axios.post(`${API}/api/bporh/folha/rubricas/`, { cliente_id: Number(clienteId), filial: filial || undefined, ...corpo },
      { headers: headers() })
      .then((r) => {
        setMensagem(`✅ ${r.data.mensagem}`);
        setRubCfg((s) => ({ ...s, rubricas: r.data.rubricas, ocupado: null,
          filiais: r.data.filiais || s.filiais, pessoas_filial: r.data.pessoas_filial || s.pessoas_filial,
          pessoas: r.data.pessoas || s.pessoas,
          modelos: (s.modelos || []).map((m) => ({ ...m,
            ja_tem: r.data.rubricas.some((x) => x.ativo && (x.rotulo || '').toUpperCase() === m.rotulo.toUpperCase()) })) }));
        carregar();            // a tabela da folha ganha/perde a coluna na hora
      })
      .catch((err) => {
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`);
        setRubCfg((s) => ({ ...s, ocupado: null }));
      });
  };

  // em qual filial a pessoa é paga (vazio volta a valer o campo da Convenia)
  const alocarFilial = (convId, filialId) => axios.post(`${API}/api/bporh/folha/rubricas/`, {
    cliente_id: Number(clienteId), filial: filial || undefined,
    acao: 'filial_colab', convenia_id: convId, filial_id: filialId || null,
  }, { headers: headers() })
    .then((r) => {
      setMensagem(`✅ ${r.data.mensagem}`);
      setRubCfg((s) => (s ? { ...s, filiais: r.data.filiais, pessoas_filial: r.data.pessoas_filial } : s));
      carregar();
    })
    .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível alocar'}`));

  // edição local de uma linha (só grava ao clicar em salvar)
  const editarRubrica = (idx, campo, valor) => setRubCfg((s) => ({
    ...s,
    rubricas: s.rubricas.map((r, i) => (i === idx ? { ...r, [campo]: valor, alterada: true } : r)),
  }));

  const novaRubrica = () => {
    setRubCfg((s) => ({
      ...s,
      rubricas: [...(s.rubricas || []), { id: null, codigo: '', rotulo: '', unidade: 'valor',
        fonte: '', parametro: '', ativo: true, alterada: true, nova: true }],
    }));
    // a linha nasce no FIM da tabela, quase sempre fora da área visível do
    // modal: rola até ela e deixa o cursor no nome da coluna
    setTimeout(() => {
      const linhas = document.querySelectorAll('[data-rubrica-nova="1"]');
      const campo = linhas[linhas.length - 1]?.querySelector('input[data-campo="rotulo"]');
      if (campo) {
        campo.scrollIntoView({ block: 'center', behavior: 'smooth' });
        campo.focus();
      }
    }, 60);
  };

  const moverRubrica = (idx, dir) => {
    const ativas = rubCfg.rubricas.filter((r) => r.ativo && r.id);
    const pos = ativas.findIndex((r) => r.id === rubCfg.rubricas[idx].id);
    const alvo = pos + dir;
    if (pos < 0 || alvo < 0 || alvo >= ativas.length) return;
    const nova = [...ativas];
    [nova[pos], nova[alvo]] = [nova[alvo], nova[pos]];
    acaoRubrica({ acao: 'ordenar', ids: nova.map((r) => r.id) }, 'ordem');
  };

  // grava o código da empresa no Domínio e o tipo de folha do cliente
  const salvarCfg = () => {
    setSalvandoCfg(true);
    axios.post(`${API}/api/bporh/folha/valor/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, acao: 'configurar',
      empresa_dominio: cfg.empresa, tipo_folha: cfg.tipo, colunas: cfg.colunas || [],
      vinculos: cfg.vinculos || [],
      prazo_dia: cfg.prazo_dia || '', prazo_mes_seguinte: cfg.prazo_mes_seguinte ? '1' : '0',
    }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setCfg(null); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvandoCfg(false));
  };

  // Um campo por tipo de arquivo. Cada escolha é LIDA sem gravar: a resposta é a
  // conferência daquele arquivo (o campo 'todos' aceita a planilha do mês inteira).
  const lerArquivo = (file, tipo, aba) => {
    if (!file) return;
    const chave = tipo || 'todos';
    setImp((s) => ({ ...(s || {}), ocupado: chave }));
    const fd = new FormData();
    fd.append('cliente_id', clienteId);
    if (filial) fd.append('filial', filial);
    fd.append('mes', mes);
    fd.append('arquivo', file);
    if (tipo) fd.append('tipo', tipo);
    if (aba) fd.append('aba', aba);
    axios.post(`${API}/api/bporh/folha/importar/`, fd, { headers: headers() })
      .then((r) => setImp((s) => ({ ...s, ocupado: null,
        previas: { ...(s.previas || {}), [chave]: { file, tipo, aba, ...r.data } } })))
      .catch((err) => setImp((s) => ({ ...s, ocupado: null,
        previas: { ...(s.previas || {}), [chave]: { file, tipo, aba, success: false,
          mensagem: err.response?.data?.mensagem || 'Não foi possível ler este arquivo' } } })));
  };

  // Grava o que já foi conferido naquele campo (substitui a importação do mês).
  const gravarArquivo = (chave) => {
    const previa = (imp.previas || {})[chave];
    if (!previa?.success) return;
    setImp((s) => ({ ...s, ocupado: chave }));
    const fd = new FormData();
    fd.append('cliente_id', clienteId);
    if (filial) fd.append('filial', filial);
    fd.append('mes', mes);
    fd.append('arquivo', previa.file);
    if (previa.tipo) fd.append('tipo', previa.tipo);
    if (previa.aba) fd.append('aba', previa.aba);
    fd.append('confirmar', '1');
    axios.post(`${API}/api/bporh/folha/importar/`, fd, { headers: headers() })
      .then((r) => {
        setMensagem(`✅ ${r.data.mensagem}`);
        setImp((s) => {
          const previas = { ...(s.previas || {}) };
          delete previas[chave];
          return { ...s, ocupado: null, previas };
        });
        carregar();
      })
      .catch((err) => {
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível importar'}`);
        setImp((s) => (s ? { ...s, ocupado: null } : s));
      });
  };

  // Zera a coluna do mês: lança 0 à mão para todo mundo (o 0 vence a fonte, então
  // a rubrica para de puxar dos módulos naquela competência).
  const zerarColuna = (rubrica, convenia_ids) => {
    setZerando(true);
    axios.post(`${API}/api/bporh/folha/valor/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, mes, acao: 'zerar_rubrica',
      rubrica_id: rubrica.id, convenia_ids,
    }, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setZerar(null); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível zerar'}`))
      .finally(() => setZerando(false));
  };

  // desfaz: tira os zeros e a coluna volta a calcular
  const voltarACalcular = (rubrica) => {
    setZerando(true);
    axios.post(`${API}/api/bporh/folha/valor/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, mes, acao: 'limpar_rubrica', rubrica_id: rubrica.id,
    }, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setZerar(null); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível limpar'}`))
      .finally(() => setZerando(false));
  };

  // Salva a FOTO do mês: valores, de onde veio cada um e o arquivo do Domínio
  // como está agora. O ponto e os módulos continuam mudando; a foto não.
  // Fechar folha = guardar a foto do mês E travar: ninguém mais lança, a tela mostra a
  // foto. Reabrir libera de novo (a foto fica no Resumo do mês).
  const fechada = !!dados?.fechada?.fechada;
  const [confirmarFechar, setConfirmarFechar] = useState(false);
  // auditoria da folha (cliente com auditoria ligada): só fecha depois do OK de todos
  const [auditoria, setAuditoria] = useState(null);
  const bloqueioAuditoria = !!auditoria && !auditoria.pode_fechar;

  // Enviar o fechamento ao DP: e-mail com o TXT do Domínio, o PDF do resumo e os
  // Excels do ponto/VA/VT que existirem. Sempre passa pela prévia — o Enviar
  // fica dentro dela.
  const [envioDp, setEnvioDp] = useState(null);   // {carregando, previa, form:{operador_id, destinatarios, copia, anexos}, enviando}
  const previaDp = (form) => {
    setEnvioDp((s) => ({ ...(s || {}), carregando: true, form: form || s?.form }));
    const corpo = { cliente_id: Number(clienteId), filial: filial || undefined, mes, enviar: false };
    if (form) {
      if (form.operador_id) corpo.operador_id = Number(form.operador_id);
      corpo.destinatarios = form.destinatarios || '';
      corpo.copia = form.copia || '';
      corpo.anexos = form.anexos;
      // na prévia só o nome dos anexos extras (para a lista do corpo); o arquivo vai no envio
      corpo.anexos_extra = (form.extras || []).map((x) => ({ nome: x.nome }));
    }
    axios.post(`${API}/api/bporh/folha/enviar_dp/`, corpo, { headers: headers() })
      .then((r) => {
        const p = r.data.previa;
        setEnvioDp((s) => ({
          carregando: false, enviando: false, previa: p,
          form: form || { operador_id: p.operador_id || '', destinatarios: p.destinatarios || '',
            copia: p.copia || '', anexos: (p.anexos || []).map((a) => a.chave), extras: [] },
        }));
      })
      .catch((err) => {
        setEnvioDp(null);
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível montar o e-mail'}`);
      });
  };
  const enviarDp = () => {
    const { previa, form } = envioDp;
    setEnvioDp((s) => ({ ...s, enviando: true }));
    axios.post(`${API}/api/bporh/folha/enviar_dp/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, mes, enviar: true,
      operador_id: Number(form.operador_id), destinatarios: form.destinatarios, copia: form.copia,
      anexos: form.anexos, assunto: previa.assunto, conteudo_html: previa.conteudo_html,
      anexos_extra: (form.extras || []).map((x) => ({ nome: x.nome, base64: x.base64 })),
    }, { headers: headers(), timeout: 300000 })
      .then((r) => { setMensagem(`📨 ${r.data.mensagem}`); setEnvioDp(null); })
      .catch((err) => {
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível enviar'}`);
        setEnvioDp((s) => ({ ...s, enviando: false }));
      });
  };
  // anexos extras (qualquer arquivo, todas as folhas): lidos no navegador e somados à
  // prévia na hora — o corpo do e-mail lista os nomes
  const EXTRA_MAX = 15 * 1024 * 1024;
  const incluirExtras = (lista) => {
    const arquivos = Array.from(lista || []);
    if (!arquivos.length) return;
    Promise.all(arquivos.map((f) => new Promise((ok, falha) => {
      const rd = new FileReader();
      rd.onload = () => ok({ nome: f.name, tamanho: f.size, base64: String(rd.result).split(',').pop() });
      rd.onerror = () => falha(f.name);
      rd.readAsDataURL(f);
    })))
      .then((novos) => {
        const form = envioDp.form || {};
        const extras = [...(form.extras || []).filter((x) => !novos.some((n) => n.nome === x.nome)), ...novos];
        if (extras.reduce((s, x) => s + x.tamanho, 0) > EXTRA_MAX) {
          setMensagem('❌ Os anexos extras passam de 15 MB');
          return;
        }
        previaDp({ ...form, extras });
      })
      .catch((nome) => setMensagem(`❌ Não foi possível ler ${nome}`));
  };
  const tirarExtra = (nome) => {
    const form = envioDp.form || {};
    previaDp({ ...form, extras: (form.extras || []).filter((x) => x.nome !== nome) });
  };
  const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const fecharOuReabrir = (acao) => {
    setSalvando(true);
    setConfirmarFechar(false);
    axios.post(`${API}/api/bporh/folha/fechamento/`, {
      cliente_id: Number(clienteId), filial: filial || undefined, mes, acao,
    }, { headers: headers(), timeout: 300000 })
      .then((r) => { setMensagem(`${acao === 'reabrir' ? '🔓' : '🔒'} ${r.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível alterar a folha'}`))
      .finally(() => setSalvando(false));
  };

  const abrirResumo = () => {
    setResumo7({ carregando: true, lista: [], foto: null });
    axios.get(`${API}/api/bporh/folha/fechamento/`, {
      params: { cliente_id: clienteId, filial: filial || undefined, mes }, headers: headers(),
    })
      .then((r) => setResumo7({ carregando: false, lista: r.data.fechamentos || [], foto: null }))
      .catch(() => { setResumo7(null); setMensagem('❌ Não foi possível abrir os fechamentos'); });
  };

  const verFoto = (id) => {
    setResumo7((s) => ({ ...s, carregando: true }));
    axios.get(`${API}/api/bporh/folha/fechamento/`, { params: { id }, headers: headers() })
      .then((r) => setResumo7((s) => ({ ...s, carregando: false, foto: r.data })))
      .catch(() => setResumo7((s) => ({ ...s, carregando: false })));
  };

  // baixa de novo o arquivo exatamente como foi salvo naquele fechamento
  const baixarTxtSalvo = (f) => {
    axios.get(`${API}/api/bporh/folha/fechamento/`, {
      params: { id: f.id, formato: 'txt' }, headers: headers(), responseType: 'blob',
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dominio_${norm(atual?.cliente || 'cliente').replace(/\s+/g, '_')}_${mes.replace('-', '_')}_v${f.versao}.txt`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => setMensagem('❌ Não foi possível baixar o arquivo salvo'));
  };

  // pendências antes de baixar: quem está sem matrícula do Domínio (fica fora do
  // arquivo) e o código da empresa. O modal mostra e deixa baixar mesmo assim.
  const [pendencias, setPendencias] = useState(null);
  const baixarTxt = () => {
    const semMat = colaboradores.filter((c) => !c.matricula);
    if (semMat.length || !arquivo.empresa_dominio || (arquivo.recusados || []).length) {
      setPendencias({ semMat, recusados: arquivo.recusados || [] });
      return;
    }
    baixarTxtAgora();
  };
  const baixarTxtAgora = () => {
    setPendencias(null);
    setBaixando(true);
    axios.get(`${API}/api/bporh/folha/`, {
      params: { cliente_id: clienteId, filial: filial || undefined, mes, formato: 'txt' },
      headers: headers(), responseType: 'blob',
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dominio_${norm(atual?.cliente || 'cliente').replace(/\s+/g, '_')}_${mes.replace('-', '_')}.txt`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        const avisos = [];
        if (!arquivo.empresa_dominio) avisos.push('sem o código da empresa no Domínio (campo saiu zerado — informe em Configurações da folha)');
        if ((arquivo.recusados || []).length) avisos.push(`${arquivo.recusados.length} linha(s) ficaram fora: ${arquivo.recusados.slice(0, 3).join('; ')}${arquivo.recusados.length > 3 ? '…' : ''}`);
        setMensagem(`${avisos.length ? '⚠️' : '✅'} Arquivo do Domínio gerado — ${arquivo.linhas} linha(s)${avisos.length ? ' · ' + avisos.join(' · ') : ''}`);
      })
      .catch(() => setMensagem('❌ Não foi possível gerar o arquivo'))
      .finally(() => setBaixando(false));
  };

  // a tabela é larga (uma coluna por rubrica): setas rolam para os lados
  const tabelaRef = useRef(null);
  const rolar = (dir) => tabelaRef.current?.scrollBy({ left: dir * 420, behavior: 'smooth' });

  // Modo TELA CHEIA: a tabela toma a janela inteira (cabeçalho e nome presos,
  // scroll nos dois eixos dentro dela). Fora dele a tabela mostra tudo e rola
  // com a página — sem altura calculada, que cortava a tabela e deixava vão embaixo.
  const [telaCheia, setTelaCheia] = useState(false);
  useEffect(() => {
    if (!telaCheia) return undefined;
    const sair = (e) => { if (e.key === 'Escape') setTelaCheia(false); };
    window.addEventListener('keydown', sair);
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';       // a página para de rolar por baixo
    return () => {
      window.removeEventListener('keydown', sair);
      document.body.style.overflow = overflowAntes;
    };
  }, [telaCheia]);

  // aviso ao entrar no módulo (14/10/2026): a folha lê VA, VT, home office e o
  // ponto — eles precisam estar feitos antes, senão a folha sai com valor faltando
  const [avisoEntrada, setAvisoEntrada] = useState(true);

  const resumo = dados?.resumo || {};
  const arquivo = dados?.arquivo || {};
  const colunas = dados?.colunas || [];
  const catalogoColunas = dados?.colunas_catalogo || {};

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-invoice-dollar header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Folha de Pagamento
              {clienteId && <span className="text-muted"> · {atual?.cliente || `empresa ${clienteId}`}</span>}
              {clienteId && dados?.filial_nome && <span style={{ color: '#00b5cc' }}> · {dados.filial_nome}</span>}
              {clienteId && dados?.mes_rotulo && <span className="text-muted"> · {dados.mes_rotulo}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteId && (
              <>
                <Link to="/Dp/Folha de Pagamento" className="btn btn-light border btn-sm">
                  <i className="fas fa-arrow-left me-2"></i>Todas as empresas
                </Link>
                {resumo.congelados > 0 && !fechada && (
                  <button className="btn btn-outline-danger btn-sm" onClick={limparAutomaticos}
                    disabled={limpando}
                    title="Apaga os valores que o 'Puxar dos módulos' congelou neste mês (o que foi digitado à mão fica)">
                    <i className={`fas ${limpando ? 'fa-spinner fa-spin' : 'fa-eraser'} me-2`}></i>
                    Limpar os {resumo.congelados} dos módulos
                  </button>
                )}
                <button className="btn btn-outline-primary btn-sm" disabled={fechada}
                  onClick={() => setImp({ previas: {}, ocupado: null })}
                  title={fechada ? 'Folha fechada — reabra para importar' : 'Plano de saúde, coparticipação, odonto, farmácia e consignado: o arquivo da operadora vira desconto na folha'}>
                  <i className="fas fa-file-arrow-up me-2"></i>Importar arquivos
                </button>
                <button className="btn btn-light border btn-sm" onClick={abrirConfig}
                  title="Rubricas (colunas) da folha e suas fontes, colunas de cadastro e arquivo do Domínio">
                  <i className="fas fa-gear me-2"></i>Configurações da folha
                  {!arquivo.empresa_dominio && (
                    <span className="badge bg-warning text-dark ms-2" title="Falta o código da empresa no Domínio">!</span>
                  )}
                </button>
                {(dados?.fechamentos || []).length > 0 && (
                  <button className="btn btn-light border btn-sm" onClick={abrirResumo}
                    title="Ver como o mês foi fechado (valores e arquivo entregues)">
                    <i className="fas fa-clock-rotate-left me-2"></i>
                    Resumo do mês ({dados.fechamentos.length})
                  </button>
                )}
                {fechada ? (
                  <button className="btn btn-outline-warning btn-sm" onClick={() => fecharOuReabrir('reabrir')}
                    disabled={salvando}
                    title="Libera a folha deste mês para novos lançamentos (a foto do fechamento fica guardada)">
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-lock-open'} me-2`}></i>
                    Reabrir folha
                  </button>
                ) : (
                  <button className="btn btn-outline-success btn-sm" onClick={() => setConfirmarFechar(true)}
                    disabled={salvando || !resumo.lancamentos || bloqueioAuditoria}
                    title={bloqueioAuditoria ? 'Esta folha passa por auditoria: o fechamento libera depois do OK de todos os auditores'
                      : 'Fecha o mês: guarda a foto (valores, origem e arquivo) e bloqueia a folha para edição'}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-lock'} me-2`}></i>
                    Fechar folha
                  </button>
                )}
                {(dados?.filiais || []).length > 1 && (
                  <button className="btn btn-outline-success btn-sm" onClick={() => setConfirmarFechar('todas')}
                    disabled={salvando || bloqueioAuditoria}
                    title={bloqueioAuditoria ? 'Esta folha passa por auditoria: o fechamento libera depois do OK de todos os auditores'
                      : 'Fecha de uma vez todas as filiais ainda abertas desta competência (cada uma guarda a sua foto)'}>
                    <i className="fas fa-lock me-2"></i>Fechar todas as filiais
                  </button>
                )}
                <button className="btn btn-outline-dark btn-sm" onClick={() => previaDp(null)}
                  disabled={!resumo.lancamentos || !!envioDp?.carregando}
                  title="E-mail ao DP com o arquivo do Domínio, o PDF do resumo e os Excels do ponto, VA e VT (o que o cliente tiver). Você vê a prévia antes.">
                  <i className={`fas ${envioDp?.carregando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  Enviar ao DP
                </button>
                <button className="btn btn-success btn-sm" onClick={baixarTxt}
                  disabled={baixando || !resumo.lancamentos}
                  title="Gera o TXT no layout de importação do Domínio (linha de 43 dígitos)">
                  <i className={`fas ${baixando ? 'fa-spinner fa-spin' : 'fa-file-arrow-down'} me-2`}></i>
                  Baixar arquivo do DP
                </button>
              </>
            )}
            <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
            </button>
          </div>
        </div>

        {carregando || !dados ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i>
            <div className="fs-5">{clienteId ? 'Carregando a folha...' : 'Carregando as empresas...'}</div>
            {clienteId && (
              <div className="small mt-2">
                Lendo os lançamentos do mês e os valores do VA, do VT e do ponto.
              </div>
            )}
          </div>
        ) : !clienteId ? (
          <>
            <div className="alert alert-light border small mb-3">
              <i className="fas fa-info-circle me-2 text-primary"></i>
              Escolha a empresa para lançar as <b>variáveis do mês</b> e baixar o arquivo do
              departamento pessoal.
            </div>
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <label className="form-label small fw-bold mb-1 text-muted">Buscar empresa</label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Empresa ou operadora..." value={busca}
                  onChange={(e) => setBusca(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th><i className="fas fa-building me-2"></i>Empresa</th>
                    <th><i className="fas fa-user-tie me-2"></i>Operadora</th>
                    <th className="text-center">Folha</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesFiltrados.map((c) => (
                    <tr key={c.id}>
                      <td className="fw-bold">{c.cliente}</td>
                      <td className="small text-muted">{c.operador || '—'}</td>
                      <td className="text-center">
                        {c.rubricas > 0 ? (
                          <Link to={`/BpoRh/Folha/cliente/${c.id}?mes=${mes}`}
                            className="btn btn-sm btn-outline-success"
                            title={`Abrir a folha de pagamento da ${c.cliente} (${c.rubricas} rubricas)`}>
                            <i className="fas fa-file-invoice-dollar me-1"></i>Abrir folha
                          </Link>
                        ) : (
                          <span className="small text-muted"
                            title="Esta empresa ainda não tem as rubricas da folha cadastradas">
                            <i className="fas fa-minus me-1"></i>sem folha
                          </span>
                        )}
                        {c.oculta ? (
                          <button type="button" className="btn btn-sm btn-link text-muted ms-2 p-0"
                            title="Trazer a empresa de volta à lista" onClick={() => ocultarEmpresa(c, false)}>
                            <i className="fas fa-eye"></i>
                          </button>
                        ) : c.rubricas === 0 && (
                          <button type="button" className="btn btn-sm btn-link text-muted ms-2 p-0"
                            title="Esconder esta empresa da lista da folha (não apaga nada)" onClick={() => ocultarEmpresa(c, true)}>
                            <i className="fas fa-eye-slash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {clientesFiltrados.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted py-4">
                      <i className="fas fa-inbox me-2"></i>Nenhuma empresa neste filtro.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {ocultas > 0 && (
              <div className="small text-muted">
                <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => setVerOcultas((v) => !v)}>
                  <i className={`fas ${verOcultas ? 'fa-eye-slash' : 'fa-eye'} me-1`}></i>
                  {verOcultas ? 'Esconder' : 'Mostrar'} {ocultas} empresa(s) fora da lista
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="alert alert-light border small mb-3">
              <i className="fas fa-info-circle me-2 text-primary"></i>
              Uma linha por colaborador e uma coluna por <b>rubrica</b> da folha, como na planilha de
              variáveis. As colunas com <i className="fas fa-bolt" style={{ color: '#0a6ea1' }}></i> são
              lidas do VA, do VT e do ponto <b>a cada vez que a tela abre</b> e aparecem
              <span style={{ color: '#0a6ea1', fontStyle: 'italic' }}> em azul</span>;
              clique no valor para ver <b>como ele foi calculado</b> e o lápis para lançar à mão.
              <b>Horas em decimal</b> (5,92 = 5h55min); valores em R$.
              Plano de saúde, coparticipação, odonto, farmácia e consignado entram por
              <b> Importar arquivos</b> — o arquivo da operadora é conferido antes de virar desconto.
              O arquivo do DP sai no <b>layout de importação do Domínio</b> (linha de 43 dígitos),
              sem as rubricas zeradas.
            </div>

            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Mês de competência</label>
                <input type="month" className="form-control form-control-sm" value={mes}
                  onChange={(e) => setMes(e.target.value)} />
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-bold mb-1 text-muted">Buscar colaborador</label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Nome, matrícula, cargo..." value={busca}
                  onChange={(e) => setBusca(e.target.value)} />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <label className="form-check-label small d-inline-flex align-items-center gap-2"
                  style={{ cursor: 'pointer' }}
                  title="Mostra apenas quem tem alguma rubrica lançada neste mês">
                  <input type="checkbox" className="form-check-input mt-0" checked={soPreenchidos}
                    onChange={(e) => setSoPreenchidos(e.target.checked)} />
                  só com lançamento
                </label>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap mb-3">
              <span className="badge fs-6" style={{ background: '#00205b' }}>
                <i className="fas fa-calendar-days me-1"></i>{dados?.mes_rotulo}
              </span>
              <span className="badge fs-6" style={{ background: '#e9ecef', color: '#212529' }}>
                <i className="fas fa-users me-1"></i>{resumo.colaboradores} colaboradores
              </span>
              <span className="badge fs-6" style={{ background: '#00b5cc' }}>
                <i className="fas fa-list-check me-1"></i>{resumo.lancamentos} lançamentos
              </span>
              {resumo.congelados > 0 && (
                <span className="badge fs-6" style={{ background: '#f1f3f5', color: '#495057', border: '1px solid #adb5bd' }}
                  title="Valores que foram congelados pelo 'Puxar dos módulos' — viraram lançamento do mês e não acompanham mais o módulo">
                  <i className="fas fa-snowflake me-1"></i>{resumo.congelados} congelado(s) dos módulos
                </span>
              )}
              {resumo.automaticos > 0 && (
                <span className="badge fs-6" style={{ background: '#e3f6fb', color: '#0a6ea1', border: '1px solid #0a6ea1' }}
                  title="Valores que vêm dos módulos (VA, VT, ponto) e ainda não foram congelados — o arquivo já os usa">
                  <i className="fas fa-bolt me-1"></i>{resumo.automaticos} automático(s)
                </span>
              )}
              {resumo.ausentes > 0 && (
                <span className="badge fs-6" style={{ background: '#e3f2fd', color: '#0a6ea1', border: '1px solid #0a6ea1' }}
                  title="Colaboradores com férias ou afastamento dentro desta competência — confira VA, VT e horas">
                  <i className="fas fa-umbrella-beach me-1"></i>
                  {resumo.ausentes} em férias/afastamento
                </span>
              )}
              {resumo.sem_matricula > 0 && (
                <span className="badge fs-6" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }}
                  title="Sem a matrícula do DOMÍNIO o colaborador não entra no arquivo do DP — informe na coluna MAT (a da Convenia não vale)">
                  <i className="fas fa-triangle-exclamation me-1"></i>
                  {resumo.sem_matricula} sem matrícula
                </span>
              )}
              {arquivo.linhas > 0 && (
                <span className="badge fs-6" style={{ background: '#e8f5e9', color: '#1b5e20', border: '1px solid #2e7d32' }}
                  title={arquivo.empresa_dominio
                    ? `Empresa ${arquivo.empresa_dominio} · ${arquivo.tipo_folha_rotulo} — cada linha tem 43 dígitos`
                    : 'Falta o código da empresa no Domínio para gerar o arquivo'}>
                  <i className="fas fa-file-lines me-1"></i>{arquivo.linhas} linha(s) no arquivo
                </span>
              )}
              {(arquivo.recusados || []).length > 0 && (
                <span className="badge fs-6" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545', cursor: 'help' }}
                  title={arquivo.recusados.join('\n')}>
                  <i className="fas fa-circle-exclamation me-1"></i>
                  {arquivo.recusados.length} fora do arquivo
                </span>
              )}
              {resumo.sem_codigo > 0 && (
                <span className="badge fs-6" style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}
                  title="Rubrica sem código não vai no arquivo — informe o código que o DP usa">
                  <i className="fas fa-hashtag me-1"></i>{resumo.sem_codigo} rubrica(s) sem código
                </span>
              )}
            </div>

            {/* o que cada módulo devolveu nesta competência (informativo) e os
                problemas (aviso) — separados, para não parecer tudo erro */}
            {Object.entries(dados?.avisos_fontes || {}).filter(([k]) => k.startsWith('_') && k !== '_competencia').length > 0 && (
              <div className="alert alert-light border small py-2">
                <i className="fas fa-bolt me-2" style={{ color: '#0a6ea1' }}></i>
                De onde vieram os valores automáticos:
                {Object.entries(dados.avisos_fontes)
                  .filter(([k]) => k.startsWith('_') && k !== '_competencia')
                  .map(([k, v]) => <div key={k} className="ms-4">· {v}</div>)}
              </div>
            )}
            {Object.entries(dados?.avisos_fontes || {}).filter(([k]) => !k.startsWith('_')).length > 0 && (
              <div className="alert alert-warning small py-2">
                <i className="fas fa-triangle-exclamation me-2"></i>
                {Object.entries(dados.avisos_fontes).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
                  <div key={k}><b>{k.toUpperCase()}:</b> {v}</div>
                ))}
              </div>
            )}

            {clienteId && !fechada && (
              <AuditoriaFolha clienteId={clienteId} mes={mes} onStatus={setAuditoria} notificar={setMensagem} />
            )}

            {fechada && (
              <div className="alert alert-secondary d-flex align-items-center flex-wrap gap-2 py-2 mb-3">
                <i className="fas fa-lock me-1"></i>
                <b>Folha de {dados.mes_rotulo} fechada</b>
                <span className="small">
                  {dados.fechada.por ? `por ${dados.fechada.por}` : ''}{dados.fechada.em ? ` em ${dados.fechada.em}` : ''} —
                  os valores mostrados são os da foto do fechamento; nada mais entra até reabrir.
                </span>
                <button type="button" className="btn btn-sm btn-outline-warning ms-auto" onClick={() => fecharOuReabrir('reabrir')} disabled={salvando}>
                  <i className="fas fa-lock-open me-1"></i>Reabrir folha
                </button>
              </div>
            )}

            {(dados?.filiais || []).length > 0 && (
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                <span className="small text-muted"><i className="fas fa-code-branch me-1"></i>Filial:</span>
                <ul className="nav nav-pills gap-1 m-0">
                  {dados.filiais.map((fl) => (
                    <li className="nav-item" key={fl.id}>
                      <button type="button"
                        className={`nav-link py-1 px-3 ${String(dados.filial) === String(fl.id) ? 'active' : 'border'}`}
                        style={String(dados.filial) === String(fl.id) ? { background: '#00b5cc' } : {}}
                        onClick={() => setFilial(fl.id)}
                        title={`Abrir a folha da ${fl.nome} (${fl.colaboradores} colaborador(es))`}>
                        {fl.nome} <span className="badge bg-light text-dark ms-1">{fl.colaboradores}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <span className="small text-muted ms-2">
                  cada filial tem as próprias rubricas, o próprio código no Domínio e o próprio arquivo
                </span>
              </div>
            )}

            {(dados?.sem_filial || []).length > 0 && (
              <div className="alert alert-warning small py-2">
                <i className="fas fa-triangle-exclamation me-2"></i>
                <b>{dados.sem_filial.length} colaborador(es) sem filial</b> — não entram em nenhuma folha
                até você dizer onde são pagos (a Convenia não tem o campo "Empresa NOME" deles):
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {dados.sem_filial.map((x) => (
                    <div key={x.convenia_id} className="d-inline-flex align-items-center gap-1 bg-white border rounded px-2 py-1">
                      <span>{x.nome}</span>
                      <select className="form-select form-select-sm py-0" style={{ width: 'auto' }} value=""
                        onChange={(e) => e.target.value && alocarFilial(x.convenia_id, Number(e.target.value))}>
                        <option value="">filial...</option>
                        {dados.filiais.map((fl) => <option key={fl.id} value={fl.id}>{fl.nome}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(dados?.fora_da_folha || []).length > 0 && (
              <div className="alert alert-light border small py-2">
                <i className="fas fa-user-slash me-2 text-muted"></i>
                <b>{dados.fora_da_folha.length} fora da folha de {dados.mes_rotulo}</b>
                {' '}(não entram na tabela nem no arquivo):
                <div className="mt-1">
                  {dados.fora_da_folha.map((x) => (
                    <button key={x.convenia_id} className="btn btn-sm btn-light border py-0 me-1 mb-1"
                      onClick={() => mudarNaFolha(x, false)}
                      title={`Trazer de volta${x.autor ? ` — tirado por ${x.autor}` : ''}`
                        + (x.em ? ` em ${x.em}` : '')}>
                      <i className="fas fa-rotate-left me-1" style={{ fontSize: '.65rem' }}></i>
                      {x.nome}{x.matricula ? ` (${x.matricula})` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={telaCheia ? 'bg-white d-flex flex-column' : ''}
              style={telaCheia ? { position: 'fixed', inset: 0, zIndex: 9000, padding: 8 } : undefined}>
              <div className="d-flex justify-content-between align-items-center gap-1 mb-1">
                <div className="small text-muted">
                  {telaCheia && (
                    <>
                      <b>{atual?.cliente}</b> · {dados?.mes_rotulo} · {linhas.length} colaborador(es)
                      <span className="ms-2">Esc sai da tela cheia</span>
                    </>
                  )}
                </div>
                <div className="d-flex gap-1">
                  <button type="button" className="btn btn-sm btn-light border" onClick={() => rolar(-1)}
                    title="Rolar a tabela para a esquerda">
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button type="button" className="btn btn-sm btn-light border" onClick={() => rolar(1)}
                    title="Rolar a tabela para a direita">
                    <i className="fas fa-chevron-right"></i>
                  </button>
                  <button type="button" className={`btn btn-sm ${telaCheia ? 'btn-dark' : 'btn-light border'}`}
                    onClick={() => setTelaCheia((v) => !v)}
                    title={telaCheia ? 'Voltar ao normal (Esc)' : 'Só a tabela na tela, com cabeçalho e nome presos'}>
                    <i className={`fas ${telaCheia ? 'fa-compress' : 'fa-expand'} me-1`}></i>
                    {telaCheia ? 'Sair' : 'Tela cheia'}
                  </button>
                </div>
              </div>
            <div className="table-responsive" ref={tabelaRef}
              style={telaCheia ? { flex: 1, minHeight: 0, overflow: 'auto' } : { overflowX: 'auto' }}>
              <table className="table table-sm table-hover align-middle mb-0" style={TABELA_FIXA}>
                <thead className="table-light">
                  <tr>
                    <th style={CANTO_FIXO}><i className="fas fa-user me-2"></i>Nome</th>
                    {colunas.map((col) => (
                      <th key={col} className={col === 'salario' ? 'text-end' : ''}
                        style={{ whiteSpace: 'nowrap', ...CABECALHO_FIXO }}>
                        {catalogoColunas[col] || col}
                      </th>
                    ))}
                    {rubricas.map((r) => (
                      <th key={r.id} className="text-center"
                        style={{ whiteSpace: 'nowrap', ...CABECALHO_FIXO }}
                        title={`${r.rotulo} — ${r.codigo ? `rubrica ${r.codigo}` : 'sem código de rubrica'} (${r.unidade_rotulo})`}>
                        <div className="small">{r.rotulo}</div>
                        {r.fora_arquivo && (
                          <div title="Não vai no arquivo do Domínio — segue numa tabela no e-mail ao DP"
                            style={{ fontSize: '.62rem', color: '#a11d2b', fontWeight: 600 }}>fora do arquivo</div>
                        )}
                        <div className="small fw-normal" style={{ color: r.codigo ? '#6c757d' : '#a11d2b' }}>
                          {!fechada && <button type="button"
                            className="btn btn-link btn-sm p-0 me-1 text-decoration-none"
                            onClick={() => setZerar({ rubrica: r })}
                            title={`Zerar ${r.rotulo} neste mês`}
                            style={{ color: '#adb5bd', lineHeight: 1 }}>
                            <i className="fas fa-circle-xmark" style={{ fontSize: '.75rem' }}></i>
                          </button>}
                          {r.codigo ? r.codigo : 'sem código'}
                          {r.fonte && (rotaDaFonte(r.fonte, clienteId, mes) ? (
                            <Link to={rotaDaFonte(r.fonte, clienteId, mes)} className="ms-1"
                              title={`${r.fonte_rotulo || 'Valor automático'} — abrir a tela de origem`}>
                              <i className="fas fa-bolt" style={{ color: '#0a6ea1' }}></i>
                            </Link>
                          ) : (
                            <i className="fas fa-bolt ms-1" style={{ color: '#0a6ea1' }}
                              title={r.fonte_rotulo || 'valor automático'}></i>
                          ))}
                        </div>
                      </th>
                    ))}
                    <th style={{ width: 44, ...CABECALHO_FIXO }}></th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((c) => (
                    <tr key={c.convenia_id}>
                      <td style={NOME_FIXO}>
                        <div className="fw-bold">{c.nome}</div>
                        {(c.alertas || []).length > 0 && (
                          <div className="mt-1" style={{ fontSize: '.72rem' }}>
                            {c.alertas.map((a, i) => <SeloAlerta key={i} a={a} />)}
                          </div>
                        )}
                        <div className="small text-muted">
                          {/* sem a coluna Matrícula ligada, a edição é aqui mesmo (BBDA, 30/09/2026:
                              o texto sob o nome não tinha clique e não dava para alterar) */}
                          {!colunas.includes('matricula') && (
                            <span className="d-inline-flex align-items-center gap-1 me-1" style={{ verticalAlign: 'middle' }}>
                              {c.matricula && <span>matrícula</span>}
                              <CelulaMatricula col={c} onSalvar={(texto) => salvarMatricula(c, texto)} />
                              <span>·</span>
                            </span>
                          )}
                          {!colunas.includes('vinculo') && c.vinculo && (
                            <span className="badge me-1" title="Tipo de contratação (vínculo na Convenia)"
                              style={{ background: /clt/i.test(c.vinculo) ? '#e3f2f6' : '#fff4e0', color: /clt/i.test(c.vinculo) ? '#0a6ea1' : '#8a6d00',
                                border: '1px solid currentColor', fontWeight: 600, fontSize: '.68rem', verticalAlign: 'middle' }}>
                              {c.vinculo}
                            </span>
                          )}
                          {!colunas.includes('cargo') && c.cargo}
                          {c.desligamento && <> · <span style={{ color: '#a11d2b' }}>saiu {c.desligamento}</span></>}
                        </div>
                      </td>
                      {colunas.map((col) => (
                        <td key={col} className={`small ${col === 'salario' ? 'text-end' : ''}`}
                          style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {col === 'matricula'
                            ? <CelulaMatricula col={c} onSalvar={(texto) => salvarMatricula(c, texto)} />
                            : valorDaColuna(c, col)}
                        </td>
                      ))}
                      {rubricas.map((r) => (
                        <td key={r.id} className="p-1">
                          <Celula valor={(c.valores || {})[r.id]} sugestao={(c.fontes || {})[r.id]}
                            unidade={r.unidade} fonte={r.fonte} fonteRotulo={r.fonte_rotulo}
                            onSalvar={(texto) => salvarValor(c, r, texto)}
                            onDetalhe={() => abrirDetalhe(c, r)} bloqueada={fechada} />
                        </td>
                      ))}
                      <td className="text-center p-1">
                        {!fechada && <button type="button" className="btn btn-sm btn-light border py-0 px-1"
                          onClick={() => mudarNaFolha(c, true)}
                          title={`Tirar ${c.nome} da folha de ${dados?.mes_rotulo || 'este mês'} `
                            + '— não entra na tabela nem no arquivo (dá para trazer de volta)'}>
                          <i className="fas fa-trash-can text-muted" style={{ fontSize: '.75rem' }}></i>
                        </button>}
                      </td>
                    </tr>
                  ))}
                  {linhas.length === 0 && (
                    <tr><td colSpan={Math.max(rubricas.length + colunas.length + 2, 3)}
                      className="text-center text-muted py-4">
                      <i className="fas fa-inbox me-2"></i>Nenhum colaborador neste filtro.
                    </td></tr>
                  )}
                </tbody>
                {linhas.length > 0 && (
                  <tfoot className="table-light">
                    <tr>
                      <th colSpan={colunas.length + 1} className="text-end"
                        style={{ ...NOME_FIXO, background: '#f8f9fa' }}>
                        Total de {linhas.length} colaborador(es)
                      </th>
                      {rubricas.map((r) => (
                        <th key={r.id} className="text-end"
                          style={{ fontVariantNumeric: 'tabular-nums', ...RODAPE }}>
                          {totais[r.id] ? paraBr(totais[r.id]) : <span className="text-muted fw-normal">—</span>}
                        </th>
                      ))}
                      <th style={RODAPE}></th>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            </div>
          </>
        )}
      </div>
      {/* ─── MEMÓRIA DE CÁLCULO (a sub-tabela da planilha) ─── */}
      {resumo7 && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 1280 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-clock-rotate-left me-2"></i>
                    {resumo7.foto ? `Fechamento v${resumo7.foto.versao}` : 'Fechamentos do mês'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {atual?.cliente}{dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                    {resumo7.foto && ` · salvo em ${resumo7.foto.salvo_em}`}
                    {resumo7.foto?.salvo_por && ` por ${resumo7.foto.salvo_por}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setResumo7(null)}></button>
              </div>
              <div className="modal-body">
                {resumo7.carregando && (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                    <div className="small">Carregando…</div>
                  </div>
                )}

                {!resumo7.carregando && !resumo7.foto && (
                  <>
                    <div className="alert alert-light border small">
                      <i className="fas fa-info-circle me-2 text-primary"></i>
                      Cada linha é uma <b>foto</b> de como a folha estava quando foi salva —
                      inclusive o arquivo do Domínio gerado na hora. O ponto e os módulos mudam
                      depois; a foto não.
                    </div>
                    <table className="table table-sm table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Versão</th><th>Salvo em</th><th>Por</th>
                          <th className="text-end">Colaboradores</th>
                          <th className="text-end">Lançamentos</th>
                          <th className="text-end">Linhas do arquivo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {resumo7.lista.map((fe) => (
                          <tr key={fe.id}>
                            <td className="fw-bold">v{fe.versao}</td>
                            <td className="small">{fe.salvo_em}</td>
                            <td className="small text-muted">{fe.salvo_por || '—'}</td>
                            <td className="text-end">{fe.colaboradores}</td>
                            <td className="text-end">{fe.lancamentos}</td>
                            <td className="text-end">{fe.linhas_arquivo}</td>
                            <td className="text-end">
                              <button className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => verFoto(fe.id)}>
                                <i className="fas fa-eye me-1"></i>Ver
                              </button>
                              {fe.tem_txt && (
                                <button className="btn btn-sm btn-outline-success"
                                  onClick={() => baixarTxtSalvo(fe)}
                                  title="Baixar o arquivo do Domínio como foi entregue">
                                  <i className="fas fa-file-arrow-down"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {resumo7.lista.length === 0 && (
                          <tr><td colSpan={7} className="text-center text-muted py-4">
                            Nenhum fechamento salvo neste mês.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}

                {!resumo7.carregando && resumo7.foto && (() => {
                  const fo = resumo7.foto.foto;
                  return (
                    <>
                      <div className="d-flex gap-2 flex-wrap mb-3">
                        <span className="badge fs-6" style={{ background: '#00205b' }}>
                          <i className="fas fa-calendar-days me-1"></i>{fo.mes_rotulo}
                        </span>
                        <span className="badge fs-6" style={{ background: '#e9ecef', color: '#212529' }}>
                          {fo.linhas.length} colaborador(es)
                        </span>
                        <span className="badge fs-6" style={{ background: '#00b5cc' }}>
                          {fo.arquivo.linhas} linha(s) no arquivo
                        </span>
                        {fo.arquivo.empresa_dominio && (
                          <span className="badge fs-6" style={{ background: '#f1f3f5', color: '#495057', border: '1px solid #adb5bd' }}>
                            Domínio {fo.arquivo.empresa_dominio} · {fo.arquivo.tipo_folha_rotulo}
                          </span>
                        )}
                      </div>
                      {(fo.importacoes || []).length > 0 && (
                        <div className="alert alert-light border small py-2">
                          <b>Arquivos usados neste fechamento:</b>
                          {fo.importacoes.map((im) => (
                            <div key={im.tipo}>· {im.arquivo}{im.aba ? ` · aba ${im.aba}` : ''} —
                              {' '}{im.linhas} linha(s), R$ {paraBr(im.total)} ({im.em})</div>
                          ))}
                        </div>
                      )}
                      <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle" style={{ minWidth: 900 }}>
                          <thead className="table-light">
                            <tr>
                              <th>Nome</th>
                              {(fo.colunas || []).map((col) => (
                                <th key={col}>{fo.colunas_catalogo?.[col] || col}</th>
                              ))}
                              {fo.rubricas.map((r) => (
                                <th key={r.id} className="text-center small">
                                  {r.rotulo}
                                  <div className="fw-normal text-muted">{r.codigo || 'sem código'}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {fo.linhas.map((l) => (
                              <tr key={l.convenia_id}>
                                <td className="fw-bold small">{l.nome}</td>
                                {(fo.colunas || []).map((col) => (
                                  <td key={col} className="small text-muted"
                                    style={{ whiteSpace: 'nowrap' }}>
                                    {valorDaColuna(l, col)}
                                  </td>
                                ))}
                                {fo.rubricas.map((r) => {
                                  const v = l.valores[String(r.id)];
                                  return (
                                    <td key={r.id} className="text-end small"
                                      style={{ fontVariantNumeric: 'tabular-nums',
                                        color: v?.origem === 'modulo' ? '#0a6ea1' : '#212529',
                                        fontStyle: v?.origem === 'modulo' ? 'italic' : 'normal' }}
                                      title={v ? (v.origem === 'modulo' ? 'veio dos módulos'
                                        : `lançado à mão${v.autor ? ` por ${v.autor}` : ''}`) : ''}>
                                      {v ? paraBr(v.valor) : <span className="text-muted">—</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="table-light">
                            <tr>
                              <th colSpan={1 + (fo.colunas || []).length} className="text-end">Totais</th>
                              {fo.rubricas.map((r) => (
                                <th key={r.id} className="text-end"
                                  style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {fo.totais?.[String(r.id)]
                                    ? paraBr(fo.totais[String(r.id)])
                                    : <span className="text-muted fw-normal">—</span>}
                                </th>
                              ))}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {(fo.arquivo.recusados || []).length > 0 && (
                        <div className="alert alert-warning small py-2 mb-0">
                          <b>Ficou fora do arquivo:</b>
                          {fo.arquivo.recusados.map((x) => <div key={x}>· {x}</div>)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                {resumo7.foto && (
                  <button className="btn btn-light border"
                    onClick={() => setResumo7((s) => ({ ...s, foto: null }))}>
                    <i className="fas fa-arrow-left me-2"></i>Todas as versões
                  </button>
                )}
                <button className="btn btn-light border" onClick={() => setResumo7(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {envioDp && (envioDp.previa || envioDp.carregando) && (() => {
        const p = envioDp.previa || {};
        const form = envioDp.form || {};
        const setForm = (patch) => setEnvioDp((s) => ({ ...s, form: { ...s.form, ...patch } }));
        const anexosSel = (p.anexos || []).filter((a) => (form.anexos || []).includes(a.chave));
        const extras = form.extras || [];
        const pronto = !envioDp.carregando && p.assunto && form.operador_id && (form.destinatarios || '').trim()
          && (anexosSel.length > 0 || extras.length > 0);
        return (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 960 }}>
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-paper-plane me-2"></i>Enviar fechamento ao DP
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {atual?.cliente}{dados?.filial_nome ? ` · ${dados.filial_nome}` : ''}{dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                      {p.fechada === false && ' · folha ainda aberta'}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setEnvioDp(null)} disabled={envioDp.enviando}></button>
                </div>
                <div className="modal-body">
                  {envioDp.carregando && !p.assunto ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-spinner fa-spin fa-2x mb-2" style={{ color: '#00b5cc' }}></i>
                      <div>Montando o arquivo, o PDF e os Excels do mês...</div>
                    </div>
                  ) : (
                    <>
                      {p.fechada === false && (
                        <div className="alert alert-warning small py-2">
                          <i className="fas fa-lock-open me-2"></i>
                          A folha deste mês <b>ainda não está fechada</b>: o que vai no e-mail é o cálculo de agora.
                          O ideal é fechar a folha antes, para o DP receber exatamente o que ficou guardado.
                        </div>
                      )}
                      <div className="row g-2 align-items-end mb-2">
                        <div className="col-md-3">
                          <label className="form-label small fw-bold mb-1 text-muted">Quem envia</label>
                          <select className="form-select form-select-sm" value={form.operador_id || ''}
                            onChange={(e) => setForm({ operador_id: e.target.value })}>
                            <option value="">— operador —</option>
                            {(p.operadores || []).map((o) => (
                              <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-bold mb-1 text-muted">Para (e-mail do DP)</label>
                          <input type="text" className="form-control form-control-sm" placeholder="dp@...; outro@..."
                            value={form.destinatarios || ''} onChange={(e) => setForm({ destinatarios: e.target.value })} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small fw-bold mb-1 text-muted">Cópia</label>
                          <input type="text" className="form-control form-control-sm" placeholder="opcional"
                            value={form.copia || ''} onChange={(e) => setForm({ copia: e.target.value })} />
                        </div>
                        <div className="col-md-2 text-end">
                          <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={() => previaDp(form)} disabled={envioDp.carregando}>
                            <i className={`fas ${envioDp.carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar prévia
                          </button>
                        </div>
                      </div>
                      <div className="form-text mb-2">Os e-mails ficam guardados para o próximo mês depois do primeiro envio.</div>

                      <div className="small fw-bold text-muted mb-1"><i className="fas fa-paperclip me-1"></i>Anexos</div>
                      <div className="border rounded p-2 mb-2">
                        {(p.anexos || []).map((a) => (
                          <label key={a.chave} className="d-flex align-items-center gap-2 small py-1" style={{ cursor: 'pointer' }}>
                            <input type="checkbox" className="form-check-input mt-0"
                              checked={(form.anexos || []).includes(a.chave)}
                              onChange={(e) => setForm({ anexos: e.target.checked
                                ? [...(form.anexos || []), a.chave] : (form.anexos || []).filter((x) => x !== a.chave) })} />
                            <i className={`fas ${a.chave === 'pdf' ? 'fa-file-pdf text-danger' : a.chave === 'txt' ? 'fa-file-lines text-secondary' : 'fa-file-excel text-success'}`}></i>
                            {a.chave === 'xlsx' && <span className="badge" style={{ background: '#fff176', color: '#212529' }}>mudou do mês anterior</span>}
                            <span><b>{a.nome}</b> <span className="text-muted">— {a.descricao}{a.linhas ? ` · ${a.linhas} linha(s)` : ''} · {kb(a.tamanho)}</span></span>
                          </label>
                        ))}
                        {(p.faltando || []).length > 0 && (
                          <div className="small text-muted mt-1">
                            <i className="fas fa-circle-info me-1"></i>Não vai (o cliente não tem esse dado no mês): {p.faltando.join('; ')}.
                          </div>
                        )}
                        <div className="border-top mt-2 pt-2">
                          {extras.map((x) => (
                            <div key={x.nome} className="d-flex align-items-center gap-2 small py-1">
                              <i className="fas fa-paperclip text-primary"></i>
                              <span><b>{x.nome}</b> <span className="text-muted">— incluído por você · {kb(x.tamanho)}</span></span>
                              <button type="button" className="btn btn-link btn-sm text-danger p-0 ms-auto"
                                onClick={() => tirarExtra(x.nome)} disabled={envioDp.carregando || envioDp.enviando}
                                title="Tirar este anexo">
                                <i className="fas fa-xmark"></i>
                              </button>
                            </div>
                          ))}
                          <label className={`btn btn-outline-secondary btn-sm mt-1 ${envioDp.carregando || envioDp.enviando ? 'disabled' : ''}`}>
                            <i className="fas fa-plus me-1"></i>Incluir outro anexo
                            <input type="file" multiple hidden
                              onChange={(e) => { incluirExtras(e.target.files); e.target.value = ''; }} />
                          </label>
                          <span className="small text-muted ms-2">qualquer arquivo · até 15 MB no total</span>
                        </div>
                      </div>

                      <div className="small fw-bold text-muted mb-1"><i className="fas fa-envelope-open-text me-1"></i>Prévia</div>
                      <div className="small mb-2">
                        <div><b>Assunto:</b> {p.assunto}</div>
                        <div><b>De:</b> {p.remetente || '—'}</div>
                        <div><b>Para:</b> {form.destinatarios || <span className="text-danger">informe o e-mail do DP</span>}</div>
                        {form.copia && <div><b>Cópia:</b> {form.copia}</div>}
                        <div><b>Anexos:</b> {anexosSel.length || extras.length
                          ? [...anexosSel.map((a) => a.nome), ...extras.map((x) => x.nome)].join(', ')
                          : <span className="text-danger">nenhum</span>}</div>
                      </div>
                      <div className="border rounded" style={{ maxHeight: 360, overflow: 'auto' }}>
                        {/* sandbox="" obrigatório: srcdoc herda a origem do app */}
                        <iframe title="Prévia do e-mail ao DP" srcDoc={p.corpo_previa || p.corpo_html}
                          sandbox="" style={{ width: '100%', height: 360, border: 0 }} />
                      </div>

                      {(p.historico || []).length > 0 && (
                        <>
                          <div className="small fw-bold text-muted mt-3 mb-1"><i className="fas fa-clock-rotate-left me-1"></i>Já enviados nesta competência</div>
                          {p.historico.map((h, i) => (
                            <div key={i} className="small text-muted border-bottom py-1">
                              <b style={{ color: '#212529' }}>{h.enviado_em}</b> · {h.destinatarios} · {h.anexos}{h.enviado_por ? ` · por ${h.enviado_por}` : ''}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef' }}>
                  <span className="small text-muted me-auto">Nada é enviado sem você clicar em Enviar.</span>
                  <button type="button" className="btn btn-light border btn-sm" onClick={() => setEnvioDp(null)} disabled={envioDp.enviando}>Fechar</button>
                  <button type="button" className="btn btn-success btn-sm" onClick={enviarDp} disabled={!pronto || envioDp.enviando}
                    title={!pronto ? 'Escolha o operador, informe o e-mail do DP e ao menos um anexo' : undefined}>
                    <i className={`fas ${envioDp.enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {envioDp.enviando ? 'Enviando...' : 'Enviar ao DP'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmarFechar && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-lock me-2"></i>Fechar a folha de {dados?.mes_rotulo}
                  </h5>
                  <small style={{ color: '#6c757d' }}>{atual?.cliente}{confirmarFechar === 'todas' ? ' · todas as filiais ainda abertas'
                    : (dados?.filial_nome ? ` · ${dados.filial_nome}` : '')}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setConfirmarFechar(false)}></button>
              </div>
              <div className="modal-body">
                {confirmarFechar === 'todas' ? (
                  <p className="mb-2">
                    Fecha <b>cada filial ainda aberta</b> desta competência ({(dados?.filiais || []).map(f => f.nome).join(', ')}):
                    cada uma guarda a sua foto (valores, origem e arquivo do Domínio) e fica <b>bloqueada</b>.
                    As que já estão fechadas não mudam.
                  </p>
                ) : (
                  <p className="mb-2">
                    Guarda a foto do mês — <b>{resumo.lancamentos} lançamento(s)</b> de{' '}
                    <b>{resumo.colaboradores} colaborador(es)</b>, {arquivo.linhas} linha(s) de arquivo — e
                    <b> bloqueia a folha</b>: ninguém mais lança, importa ou zera nesta competência.
                  </p>
                )}
                <div className="small text-muted">
                  Os valores que vêm do ponto, do VA e do VT ficam congelados como estão agora.
                  Se precisar mexer depois, é só clicar em <b>Reabrir folha</b>.
                </div>
                {resumo.sem_matricula > 0 && (
                  <div className="alert alert-warning small py-2 mt-3 mb-0">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    {resumo.sem_matricula} colaborador(es) sem matrícula do Domínio ficam fora do arquivo.
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef' }}>
                <button type="button" className="btn btn-light border btn-sm" onClick={() => setConfirmarFechar(false)}>Cancelar</button>
                <button type="button" className="btn btn-success btn-sm"
                  onClick={() => fecharOuReabrir(confirmarFechar === 'todas' ? 'fechar_todas' : 'fechar')} disabled={salvando}>
                  <i className="fas fa-lock me-2"></i>{confirmarFechar === 'todas' ? 'Fechar todas as filiais' : 'Fechar folha'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendencias && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 680 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-triangle-exclamation me-2 text-warning"></i>Arquivo com pendências
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {atual?.cliente}{dados?.filial_nome ? ` · ${dados.filial_nome}` : ''}{dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setPendencias(null)}></button>
              </div>
              <div className="modal-body">
                {!arquivo.empresa_dominio && (
                  <div className="alert alert-warning small py-2">
                    <i className="fas fa-building me-2"></i>
                    <b>Sem o código da empresa no Domínio</b> — o campo Empresa vai sair zerado
                    (<code>0000000000</code>). Informe em Configurações da folha quando tiver.
                  </div>
                )}
                {pendencias.semMat.length > 0 && (
                  <>
                    <p className="mb-2">
                      <b>{pendencias.semMat.length} colaborador(es) sem número de matrícula no Domínio.</b>{' '}
                      Sem a matrícula o Domínio não sabe de quem é a linha, então eles <b>ficam fora do arquivo</b>:
                    </p>
                    <div className="border rounded p-2 mb-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {pendencias.semMat.map((c) => (
                        <div key={c.convenia_id} className="d-flex justify-content-between small py-1 border-bottom">
                          <span>{c.nome}</span>
                          <span className="text-muted">
                            {c.matricula_convenia ? `Convenia: ${c.matricula_convenia}` : 'sem matrícula'}
                            {Object.values(c.valores || {}).some((v) => v) || Object.values(c.fontes || {}).some((v) => v)
                              ? <span className="text-danger ms-2" title="Tem lançamento neste mês que vai ficar de fora">· com lançamento</span> : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="small text-muted mb-2">
                      Para incluir: clique na célula MAT da linha e digite a matrícula do Domínio (ela fica gravada).
                    </div>
                  </>
                )}
                {pendencias.recusados.filter((r) => !/sem matrícula/.test(r)).length > 0 && (
                  <div className="alert alert-light border small py-2 mb-0">
                    <b>Outras linhas fora do arquivo:</b>
                    <ul className="mb-0 mt-1">
                      {pendencias.recusados.filter((r) => !/sem matrícula/.test(r)).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef' }}>
                <span className="small text-muted me-auto">O arquivo sai com {arquivo.linhas} linha(s).</span>
                <button type="button" className="btn btn-light border btn-sm" onClick={() => setPendencias(null)}>Cancelar</button>
                <button type="button" className="btn btn-success btn-sm" onClick={baixarTxtAgora} disabled={baixando}>
                  <i className="fas fa-file-arrow-down me-2"></i>Baixar arquivo mesmo assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {zerar && (() => {
        const rub = zerar.rubrica;
        const comValor = colaboradores.filter(
          (c) => ((c.valores || {})[rub.id] ?? (c.fontes || {})[rub.id]));
        const zerados = colaboradores.filter((c) => (c.valores || {})[rub.id] === 0);
        return (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 680 }}>
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-circle-xmark me-2"></i>Zerar {rub.rotulo}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {atual?.cliente}{dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                      {rub.codigo ? ` · rubrica ${rub.codigo}` : ''}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setZerar(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    Lança <b>0,00</b> nesta coluna para <b>{comValor.length}</b> colaborador(es)
                    com valor neste mês.
                  </p>
                  {rub.fonte && (
                    <div className="alert alert-warning small py-2">
                      <i className="fas fa-triangle-exclamation me-2"></i>
                      Esta coluna se abastece sozinha ({rub.fonte_rotulo}). O zero lançado à mão
                      <b> vence a fonte</b>: nesta competência ela para de puxar do módulo até você
                      mandar calcular de novo.
                    </div>
                  )}
                  <div className="small text-muted">
                    Rubrica zerada não entra no arquivo do Domínio — é assim que se diz ao DP que
                    o mês não tem esse lançamento.
                  </div>
                  {zerados.length > 0 && (
                    <div className="alert alert-light border small py-2 mt-3 mb-0">
                      Já existem <b>{zerados.length}</b> lançamento(s) zerado(s) nesta coluna.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {zerados.length > 0 && (
                    <button className="btn btn-outline-secondary" disabled={zerando}
                      onClick={() => voltarACalcular(rub)}
                      title="Tira os zeros e a coluna volta a calcular sozinha">
                      <i className={`fas ${zerando ? 'fa-spinner fa-spin' : 'fa-rotate-left'} me-2`}></i>
                      Voltar a calcular
                    </button>
                  )}
                  <button className="btn btn-light border" onClick={() => setZerar(null)}>Cancelar</button>
                  <button className="btn btn-danger" disabled={zerando || !comValor.length}
                    onClick={() => zerarColuna(rub, comValor.map((c) => c.convenia_id))}>
                    <i className={`fas ${zerando ? 'fa-spinner fa-spin' : 'fa-circle-xmark'} me-2`}></i>
                    Zerar a coluna
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {imp && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 1280 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-arrow-up me-2"></i>Arquivos do mês
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {atual?.cliente}{dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                    {' · cada arquivo no seu campo; nada é gravado antes de você conferir'}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setImp(null)}></button>
              </div>
              <div className="modal-body">
                {/* UM campo para tudo: o operador diz em qual coluna o arquivo entra.
                    Coluna ligada a um arquivo de benefício segue o caminho daquele
                    tipo (regra de percentual, memória de cálculo); as demais entram
                    como lançamento da célula. */}
                {(() => {
                  const rub = (dados?.rubricas || []).find((r) => String(r.id) === String(rubricaAlvo));
                  const cat = Object.entries(dados?.importacoes_catalogo || {})
                    .find(([, c]) => rub && c.fonte === rub.fonte);
                  const tipoArquivo = cat ? cat[0] : null;
                  return (
                    <BlocoImportacao chave={`rubrica:${rubricaAlvo || ''}`}
                      titulo={rub ? `Importar para ${rub.rotulo}` : 'Importar planilha ou PDF'}
                      icone={cat ? cat[1].icone : 'fa-file-circle-plus'}
                      dica={cat
                        ? cat[1].dica
                        : 'Qualquer arquivo com o nome do colaborador e o valor — Excel, CSV ou PDF. A IA lê e mostra o que entendeu antes de lançar.'}
                      regra={cat ? cat[1].regra
                        : 'O valor vai inteiro para a coluna escolhida, somando quando a pessoa aparece mais de uma vez'}
                      rubrica={rub}
                      jaImportado={tipoArquivo ? (dados?.importacoes || {})[tipoArquivo] : null}
                      imp={imp} dadosFolha={dados}
                      onEscolher={(file, aba) => {
                        if (!rubricaAlvo) { setMensagem('⚠️ Escolha primeiro a coluna de destino'); return; }
                        lerArquivo(file, `rubrica:${rubricaAlvo}`, aba);
                      }}
                      onGravar={() => gravarArquivo(`rubrica:${rubricaAlvo}`)}
                      extraCabecalho={(
                        <div className="mt-2">
                          <label className="form-label small fw-bold mb-1 text-muted">
                            Para qual coluna da folha vai este arquivo?
                          </label>
                          <select className="form-select" style={{ maxWidth: 520 }}
                            value={rubricaAlvo || ''}
                            onChange={(e) => setRubricaAlvo(e.target.value)}>
                            <option value="">— escolha a coluna —</option>
                            {(dados?.rubricas || []).map((r) => {
                              const c = Object.values(dados?.importacoes_catalogo || {})
                                .find((x) => x.fonte === r.fonte);
                              return (
                                <option key={r.id} value={r.id}>
                                  {r.rotulo}{r.codigo ? ` (${r.codigo})` : ''}
                                  {c ? ` — ${c.rotulo}` : ''}
                                </option>
                              );
                            })}
                          </select>
                          {rub && !cat && (
                            <div className="form-text">
                              O valor lido entra como lançamento desta coluna, igual ao que você
                              digitaria na tabela.
                            </div>
                          )}
                        </div>
                      )} />
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button className="btn btn-light border" onClick={() => setImp(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {cfg && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 1280 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-gear me-2"></i>Configurações da folha
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {atual?.cliente}{dados?.filial_nome ? ` · ${dados.filial_nome}` : ''} · rubricas e fontes, colunas de cadastro e arquivo do Domínio
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setCfg(null)}></button>
              </div>
              <div className="modal-body">
                {/* ── 1. Rubricas: as colunas da folha e de onde cada uma se abastece ── */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div>
                    <div className="fw-bold"><i className="fas fa-table-columns me-2 text-muted"></i>Rubricas da folha</div>
                    <div className="small text-muted">
                      Uma por coluna, na ordem da planilha do cliente. A <b>fonte</b> diz de onde o valor vem
                      sozinho (VA, VT, ponto, arquivos importados); sem fonte, é digitado à mão.
                      O <b>código</b> é o da rubrica no Domínio — sem ele a coluna não entra no arquivo.
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline-primary" onClick={novaRubrica}
                    disabled={!rubCfg || rubCfg.carregando}>
                    <i className="fas fa-plus me-1"></i>Nova rubrica
                  </button>
                </div>

                {rubCfg?.carregando && (
                  <div className="text-center text-muted py-3"><i className="fas fa-spinner fa-spin me-2"></i>Carregando…</div>
                )}

                {rubCfg && !rubCfg.carregando && (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-2">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 64 }}>Ordem</th>
                            <th style={{ width: 90 }}>Código</th>
                            <th style={{ minWidth: 180 }}>Nome da coluna</th>
                            <th style={{ width: 110 }}>Unidade</th>
                            <th style={{ minWidth: 260 }}>Fonte (de onde vem)</th>
                            <th style={{ minWidth: 150 }}>Parâmetro</th>
                            <th style={{ width: 110 }} className="text-center"
                              title="Desmarcado: a coluna fica na folha mas NÃO vai no arquivo do Domínio; vai numa tabela no e-mail ao DP">No arquivo</th>
                            <th style={{ width: 120 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rubCfg.rubricas.filter((r) => r.ativo).map((r) => {
                            const idx = rubCfg.rubricas.indexOf(r);
                            const fonteInfo = (rubCfg.fontes || []).find((f) => f.id === r.fonte);
                            const grupos = [...new Set((rubCfg.fontes || []).map((f) => f.grupo))];
                            return (
                              <tr key={r.id || `nova-${idx}`} data-rubrica-nova={r.nova ? '1' : undefined}
                                style={r.alterada ? { background: '#fff8e6' } : undefined}>
                                <td className="text-nowrap">
                                  {r.id && (
                                    <>
                                      <button className="btn btn-sm btn-light border py-0 px-1 me-1"
                                        onClick={() => moverRubrica(idx, -1)} title="Mover para a esquerda"
                                        disabled={rubCfg.ocupado === 'ordem'}>
                                        <i className="fas fa-arrow-up" style={{ fontSize: '.7rem' }}></i>
                                      </button>
                                      <button className="btn btn-sm btn-light border py-0 px-1"
                                        onClick={() => moverRubrica(idx, 1)} title="Mover para a direita"
                                        disabled={rubCfg.ocupado === 'ordem'}>
                                        <i className="fas fa-arrow-down" style={{ fontSize: '.7rem' }}></i>
                                      </button>
                                    </>
                                  )}
                                </td>
                                <td>
                                  <input type="text" inputMode="numeric" className="form-control form-control-sm"
                                    placeholder="ex.: 216" maxLength={4} value={r.codigo || ''}
                                    style={!r.codigo ? { borderColor: '#f1be48' } : undefined}
                                    title={!r.codigo ? 'Sem código a coluna não entra no arquivo do Domínio' : ''}
                                    onChange={(e) => editarRubrica(idx, 'codigo', e.target.value.replace(/\D/g, ''))} />
                                </td>
                                <td>
                                  <input type="text" className="form-control form-control-sm"
                                    data-campo="rotulo"
                                    placeholder="ex.: PLANO DE SAUDE" value={r.rotulo || ''}
                                    onChange={(e) => editarRubrica(idx, 'rotulo', e.target.value)} />
                                </td>
                                <td>
                                  <select className="form-select form-select-sm" value={r.unidade || 'valor'}
                                    onChange={(e) => editarRubrica(idx, 'unidade', e.target.value)}>
                                    {Object.entries(rubCfg.unidades || { valor: 'R$', horas: 'horas' }).map(([k, v]) => (
                                      <option key={k} value={k}>{v}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <select className="form-select form-select-sm" value={r.fonte || ''}
                                    onChange={(e) => {
                                      const nova = e.target.value;
                                      const info = (rubCfg.fontes || []).find((f) => f.id === nova);
                                      editarRubrica(idx, 'fonte', nova);
                                      // parâmetro padrão da fonte, se a rubrica ainda não tiver um
                                      if (info?.parametro && (r.parametro == null || r.parametro === '')) {
                                        editarRubrica(idx, 'parametro', info.parametro.padrao);
                                      }
                                    }}>
                                    <option value="">— digitado à mão —</option>
                                    {grupos.map((g) => (
                                      <optgroup key={g} label={g}>
                                        {(rubCfg.fontes || []).filter((f) => f.grupo === g).map((f) => (
                                          <option key={f.id} value={f.id}>{f.rotulo}</option>
                                        ))}
                                      </optgroup>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  {fonteInfo?.parametro ? (
                                    <>
                                      <input type="text" inputMode="decimal" className="form-control form-control-sm"
                                        value={r.parametro ?? ''}
                                        placeholder={String(fonteInfo.parametro.padrao)}
                                        onChange={(e) => editarRubrica(idx, 'parametro', e.target.value)} />
                                      <div className="text-muted" style={{ fontSize: '.7rem', lineHeight: 1.1 }}>
                                        {fonteInfo.parametro.rotulo}
                                      </div>
                                    </>
                                  ) : (
                                    /* valor PADRÃO da coluna: entra para todo ativo onde a fonte
                                       não trouxer nada — R$ fixo por mês ou % do salário base */
                                    <div className="d-flex gap-1 align-items-start">
                                      <select className="form-select form-select-sm" style={{ width: 118 }}
                                        value={r.parametro_tipo || ''}
                                        onChange={(e) => {
                                          editarRubrica(idx, 'parametro_tipo', e.target.value || null);
                                          if (!e.target.value) editarRubrica(idx, 'parametro', '');
                                        }}>
                                        <option value="">— nenhum —</option>
                                        <option value="valor">R$ por mês</option>
                                        <option value="por_dia">R$ por dia trabalhado</option>
                                        <option value="percentual">% do salário</option>
                                        <option value="quinquenio">% do salário (5+ anos de casa)</option>
                                      </select>
                                      {r.parametro_tipo && (
                                        <div>
                                          <input type="text" inputMode="decimal" className="form-control form-control-sm"
                                            style={{ width: 90 }} value={r.parametro ?? ''}
                                            placeholder={r.parametro_tipo === 'percentual' ? '6' : r.parametro_tipo === 'quinquenio' ? '3' : (r.parametro_tipo === 'por_dia' ? '25,00' : '130,00')}
                                            onChange={(e) => editarRubrica(idx, 'parametro', e.target.value)} />
                                          <div className="text-muted" style={{ fontSize: '.68rem', lineHeight: 1.1 }}>
                                            {r.parametro_tipo === 'valor'
                                              ? 'para todo ativo; a fonte vence quando tiver valor'
                                              : r.parametro_tipo === 'por_dia'
                                                ? '× dias úteis trabalhados no mês (menos férias/afastamento, proporcional a admissão e desligamento)'
                                                : r.parametro_tipo === 'quinquenio'
                                                  ? 'do salário base, só para quem completou 5 anos de casa até o fim do mês'
                                                  : 'do salário base da Convenia; a fonte vence'}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="text-center">
                                  {/* fora do arquivo (ex.: plano de saúde): fica na folha, sai do TXT
                                      e vai na tabela "Não está no arquivo" do e-mail ao DP */}
                                  <div className="form-check form-switch d-inline-block mb-0">
                                    <input className="form-check-input" type="checkbox" checked={!r.fora_arquivo}
                                      disabled={rubCfg.ocupado === `arquivo-${idx}`}
                                      onChange={(e) => (r.id && !r.alterada
                                        ? acaoRubrica({ acao: 'salvar', rubrica: { ...r, fora_arquivo: !e.target.checked } }, `arquivo-${idx}`)
                                        : editarRubrica(idx, 'fora_arquivo', !e.target.checked))} />
                                  </div>
                                  {r.fora_arquivo && <div style={{ fontSize: '.68rem', color: '#a11d2b', lineHeight: 1.1 }}>fora do arquivo · vai no e-mail</div>}
                                </td>
                                <td className="text-end text-nowrap">
                                  {r.alterada && (
                                    <button className="btn btn-sm btn-success me-1"
                                      disabled={rubCfg.ocupado === `salvar-${idx}` || !(r.rotulo || '').trim()}
                                      onClick={() => acaoRubrica({ acao: 'salvar', rubrica: r }, `salvar-${idx}`)}
                                      title="Gravar esta rubrica">
                                      <i className={`fas ${rubCfg.ocupado === `salvar-${idx}` ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                                    </button>
                                  )}
                                  {r.id ? (
                                    <button className="btn btn-sm btn-outline-danger"
                                      disabled={rubCfg.ocupado === `remover-${r.id}`}
                                      onClick={() => acaoRubrica({ acao: 'remover', id: r.id }, `remover-${r.id}`)}
                                      title="Tirar esta coluna da folha (os lançamentos ficam guardados)">
                                      <i className="fas fa-trash-can"></i>
                                    </button>
                                  ) : (
                                    <button className="btn btn-sm btn-light border"
                                      onClick={() => setRubCfg((s) => ({ ...s, rubricas: s.rubricas.filter((_, i) => i !== idx) }))}
                                      title="Descartar">
                                      <i className="fas fa-xmark"></i>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {rubCfg.rubricas.filter((r) => r.ativo).length === 0 && (
                            <tr><td colSpan={8} className="text-center text-muted py-3">
                              Nenhuma rubrica ainda — crie uma ou use os modelos abaixo.
                            </td></tr>
                          )}
                          <tr>
                            <td colSpan={8} className="py-1">
                              <button className="btn btn-sm btn-outline-primary" onClick={novaRubrica}>
                                <i className="fas fa-plus me-1"></i>Nova rubrica
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {rubCfg.rubricas.some((r) => !r.ativo) && (
                      <div className="small text-muted mb-2">
                        <i className="fas fa-eye-slash me-1"></i>Retiradas da folha (os lançamentos ficam guardados):
                        {rubCfg.rubricas.filter((r) => !r.ativo).map((r) => (
                          <span key={r.id} className="btn-group btn-group-sm ms-1 mb-1" role="group">
                            <button className="btn btn-sm btn-light border py-0"
                              disabled={rubCfg.ocupado === `reativar-${r.id}`}
                              onClick={() => acaoRubrica({ acao: 'salvar', rubrica: { ...r, ativo: true } }, `reativar-${r.id}`)}
                              title="Voltar com esta coluna">
                              <i className="fas fa-rotate-left me-1" style={{ fontSize: '.65rem' }}></i>
                              {r.rotulo}{r.codigo ? ` (${r.codigo})` : ''}
                            </button>
                            <button className="btn btn-sm btn-light border py-0 text-danger"
                              onClick={() => setRubCfg((s) => ({ ...s, excluir: r }))}
                              title="Excluir esta coluna de vez (apaga os valores lançados nela)">
                              <i className="fas fa-trash" style={{ fontSize: '.65rem' }}></i>
                            </button>
                          </span>
                        ))}
                        {rubCfg.excluir && (
                          <div className="alert alert-danger small py-2 mt-2 mb-0 d-flex align-items-center flex-wrap gap-2">
                            <span>
                              <i className="fas fa-triangle-exclamation me-1"></i>
                              Excluir <b>{rubCfg.excluir.rotulo}{rubCfg.excluir.codigo ? ` (${rubCfg.excluir.codigo})` : ''}</b> de vez?
                              {' '}A coluna e todos os valores lançados nela são apagados, sem volta.
                              Meses já fechados não mudam.
                            </span>
                            <button className="btn btn-sm btn-danger py-0"
                              disabled={rubCfg.ocupado === `excluir-${rubCfg.excluir.id}`}
                              onClick={() => acaoRubrica({ acao: 'excluir', id: rubCfg.excluir.id }, `excluir-${rubCfg.excluir.id}`)
                                .then(() => setRubCfg((s) => ({ ...s, excluir: null })))}>
                              <i className="fas fa-trash me-1"></i>Excluir de vez
                            </button>
                            <button className="btn btn-sm btn-light border py-0"
                              onClick={() => setRubCfg((s) => ({ ...s, excluir: null }))}>Cancelar</button>
                          </div>
                        )}
                      </div>
                    )}

                    {(rubCfg.modelos || []).some((m) => !m.ja_tem) && (
                      <div className="border rounded p-2 mb-3" style={{ background: '#f8fbfd' }}>
                        <div className="small fw-bold text-muted mb-1">
                          <i className="fas fa-wand-magic-sparkles me-1"></i>
                          Modelos prontos — um clique cria a coluna já ligada na fonte certa
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {rubCfg.modelos.filter((m) => !m.ja_tem).map((m) => (
                            <button key={m.chave} className="btn btn-sm btn-outline-secondary py-0"
                              disabled={rubCfg.ocupado === `modelo-${m.chave}`}
                              onClick={() => acaoRubrica({ acao: 'modelos', chaves: [m.chave] }, `modelo-${m.chave}`)}
                              title={`${m.fonte ? 'Fonte: ' + ((rubCfg.fontes || []).find((f) => f.id === m.fonte)?.rotulo || m.fonte) : 'Digitada à mão'}${m.codigo ? ' · código ' + m.codigo : ' · sem código (informe)'}`}>
                              <i className="fas fa-plus me-1" style={{ fontSize: '.65rem' }}></i>
                              {m.rotulo}{m.codigo ? ` (${m.codigo})` : ''}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <hr className="my-3" />
                {/* ── Pessoas sem Convenia: prestadores de RPA e quem mais for pago pela folha ── */}
                <div className="fw-bold mb-1"><i className="fas fa-user-pen me-2 text-muted"></i>Colaboradores cadastrados à mão</div>
                <div className="small text-muted mb-2">
                  Quem não está na Convenia mas entra na folha — prestadores de <b>RPA</b>, por exemplo.
                  Entram na tabela, no arquivo do Domínio e no fechamento como os demais; o vínculo obedece
                  aos checkboxes de vínculo abaixo. Edite direto no campo (grava ao sair dele).
                </div>
                {(rubCfg?.pessoas || []).length > 0 && (
                  <div className="table-responsive mb-2">
                    <table className="table table-sm align-middle mb-0" style={{ fontSize: '.85rem' }}>
                      <thead className="table-light"><tr>
                        <th>Nome</th><th style={{ width: 150 }}>CPF</th><th style={{ width: 110 }}>Matrícula (Domínio)</th>
                        <th style={{ width: 170 }}>Vínculo</th><th style={{ width: 130 }} className="text-end">Valor mensal</th><th style={{ width: 40 }}></th>
                      </tr></thead>
                      <tbody>
                        {rubCfg.pessoas.map((p, idx) => {
                          const salvar = () => p._mudou && acaoRubrica({ acao: 'pessoa_salvar', pessoa: p }, `pes${p.id}`);
                          return (
                            <tr key={p.id}>
                              <td><input type="text" className="form-control form-control-sm" value={p.nome || ''}
                                onChange={(e) => editarPessoa(idx, 'nome', e.target.value)} onBlur={salvar} /></td>
                              <td><input type="text" className="form-control form-control-sm" value={p.cpf || ''} placeholder="só números"
                                onChange={(e) => editarPessoa(idx, 'cpf', e.target.value.replace(/\D/g, '').slice(0, 11))} onBlur={salvar} /></td>
                              <td><input type="text" className={`form-control form-control-sm ${!p.matricula ? 'border-danger' : ''}`} value={p.matricula || ''}
                                onChange={(e) => editarPessoa(idx, 'matricula', e.target.value.replace(/\D/g, ''))} onBlur={salvar} /></td>
                              <td>
                                <select className="form-select form-select-sm" value={p.vinculo || ''}
                                  onChange={(e) => { editarPessoa(idx, 'vinculo', e.target.value); }} onBlur={salvar}>
                                  {Object.values(rubCfg.vinculos_catalogo || {}).filter((v) => !/Sem vínculo|Outros/.test(v)).map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </td>
                              <td><input type="text" inputMode="decimal" className="form-control form-control-sm text-end"
                                value={p.salario ?? ''} placeholder="0,00"
                                onChange={(e) => editarPessoa(idx, 'salario', e.target.value)} onBlur={salvar} /></td>
                              <td className="text-end">
                                <button type="button" className="btn btn-sm btn-link text-danger p-0" title="Tirar da folha"
                                  onClick={() => acaoRubrica({ acao: 'pessoa_remover', id: p.id }, `pes${p.id}`)}>
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
                  <input type="text" className="form-control form-control-sm" style={{ width: 240 }} placeholder="Nome completo"
                    value={novaPessoa.nome} onChange={(e) => setNovaPessoa({ ...novaPessoa, nome: e.target.value })} />
                  <input type="text" className="form-control form-control-sm" style={{ width: 130 }} placeholder="CPF (só números)"
                    value={novaPessoa.cpf} onChange={(e) => setNovaPessoa({ ...novaPessoa, cpf: e.target.value.replace(/\D/g, '').slice(0, 11) })} />
                  <input type="text" className="form-control form-control-sm" style={{ width: 120 }} placeholder="Matrícula Domínio"
                    value={novaPessoa.matricula} onChange={(e) => setNovaPessoa({ ...novaPessoa, matricula: e.target.value.replace(/\D/g, '') })} />
                  <select className="form-select form-select-sm" style={{ width: 170 }} value={novaPessoa.vinculo}
                    onChange={(e) => setNovaPessoa({ ...novaPessoa, vinculo: e.target.value })}>
                    {Object.values(rubCfg?.vinculos_catalogo || { autonomo: 'Autônomo' }).filter((v) => !/Sem vínculo|Outros/.test(v)).map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <input type="text" inputMode="decimal" className="form-control form-control-sm" style={{ width: 110 }} placeholder="Valor mensal"
                    value={novaPessoa.salario} onChange={(e) => setNovaPessoa({ ...novaPessoa, salario: e.target.value })} />
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={!novaPessoa.nome.trim()}
                    onClick={() => acaoRubrica({ acao: 'pessoa_salvar', pessoa: novaPessoa }, 'pesnova').then(() => setNovaPessoa(PESSOA_VAZIA))}>
                    <i className="fas fa-user-plus me-1"></i>Adicionar
                  </button>
                </div>

                <hr className="my-3" />
                {/* ── Filiais: cliente com mais de uma empresa no Domínio tem uma folha por filial ── */}
                <div className="fw-bold mb-1"><i className="fas fa-code-branch me-2 text-muted"></i>Filiais</div>
                <div className="small text-muted mb-2">
                  Empresa com mais de um código no Domínio (matriz e filiais) tem <b>uma folha por filial</b>:
                  rubricas, código da empresa, importações e fechamentos separados. Quem é pago em qual filial
                  vem do campo "Empresa NOME" da Convenia; aqui você corrige quem estiver errado ou sem o campo.
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  {(rubCfg?.filiais || []).map((fl) => (
                    <div key={fl.id} className={`d-inline-flex align-items-center gap-1 border rounded px-2 py-1 ${String(dados?.filial) === String(fl.id) ? 'border-info' : ''}`}>
                      <input type="text" className="form-control form-control-sm border-0 p-0" style={{ width: 150 }}
                        defaultValue={fl.nome}
                        onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== fl.nome
                          && acaoRubrica({ acao: 'filial_salvar', filial: { id: fl.id, nome: e.target.value.trim() } }, `fil${fl.id}`)} />
                      <button type="button" className="btn btn-sm btn-link text-danger p-0 ms-1" title="Retirar esta filial"
                        onClick={() => acaoRubrica({ acao: 'filial_remover', id: fl.id }, `fil${fl.id}`)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                  <div className="d-inline-flex align-items-center gap-1">
                    <input type="text" className="form-control form-control-sm" style={{ width: 170 }}
                      placeholder="Nova filial (ex.: Filial Lavras)" value={novaFilial}
                      onChange={(e) => setNovaFilial(e.target.value)} />
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!novaFilial.trim()}
                      onClick={() => acaoRubrica({ acao: 'filial_salvar', filial: { nome: novaFilial.trim() } }, 'filnova')
                        .then(() => setNovaFilial(''))}>
                      <i className="fas fa-plus me-1"></i>Criar
                    </button>
                  </div>
                </div>
                {(rubCfg?.pessoas_filial || []).length > 0 && (
                  <div className="border rounded p-2 mb-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                    <div className="small fw-bold text-muted mb-1">Quem é pago em qual filial</div>
                    <div className="row g-1">
                      {rubCfg.pessoas_filial.map((p) => (
                        <div key={p.convenia_id} className="col-md-4 d-flex align-items-center gap-2 small">
                          <select className={`form-select form-select-sm py-0 ${!p.filial_id ? 'border-warning' : ''}`}
                            style={{ width: 150 }} value={p.filial_id || ''}
                            onChange={(e) => alocarFilial(p.convenia_id, e.target.value ? Number(e.target.value) : null)}>
                            <option value="">— sem filial —</option>
                            {(rubCfg.filiais || []).map((fl) => <option key={fl.id} value={fl.id}>{fl.nome}</option>)}
                          </select>
                          <span className="text-truncate" title={`${p.nome}${p.filial_convenia ? ` · Convenia: ${p.filial_convenia}` : ''}${p.filial_origem === 'manual' ? ' · escolhido aqui' : ''}`}>
                            {p.filial_origem === 'manual' && <i className="fas fa-thumbtack me-1 text-muted" style={{ fontSize: '.65rem' }}></i>}
                            {p.nome}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <hr className="my-3" />
                {/* ── 2 e 3. Arquivo do Domínio e colunas de cadastro ── */}
                <div className="fw-bold mb-2"><i className="fas fa-file-import me-2 text-muted"></i>Arquivo de importação do Domínio{dados?.filial_nome ? ` — ${dados.filial_nome}` : ''}</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label small fw-bold mb-1 text-muted">
                      Código da empresa no Domínio
                    </label>
                    <input type="text" inputMode="numeric" className="form-control"
                      placeholder="ex.: 683" maxLength={10} value={cfg.empresa}
                      onChange={(e) => setCfg({ ...cfg, empresa: e.target.value.replace(/\D/g, '') })} />
                    <div className="form-text">É o código do cadastro da empresa no Domínio (até 10 dígitos).</div>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-bold mb-1 text-muted">Tipo de folha</label>
                    <select className="form-select" value={cfg.tipo}
                      onChange={(e) => setCfg({ ...cfg, tipo: e.target.value })}>
                      {Object.entries(arquivo.tipos_folha || { 11: 'Mensal' }).map(([k, v]) => (
                        <option key={k} value={k}>{v} ({k})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <hr className="my-3" />
                <div className="fw-bold mb-1"><i className="fas fa-calendar-check me-2 text-muted"></i>Prazo de fechamento da folha</div>
                <div className="small text-muted mb-2">
                  Até que dia a folha desta empresa tem de estar <b>fechada</b>. O Painel do Operador avisa a 3 dias
                  e fica vermelho quando passa — e aí pede a justificativa do atraso.
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <span className="small">Dia</span>
                  <input type="text" inputMode="numeric" className="form-control form-control-sm" style={{ width: 70 }}
                    placeholder="30" value={cfg.prazo_dia || ''}
                    onChange={(e) => setCfg({ ...cfg, prazo_dia: e.target.value.replace(/\D/g, '').slice(0, 2) })} />
                  <select className="form-select form-select-sm" style={{ width: 'auto' }}
                    value={cfg.prazo_mes_seguinte ? '1' : '0'}
                    onChange={(e) => setCfg({ ...cfg, prazo_mes_seguinte: e.target.value === '1' })}>
                    <option value="0">do próprio mês da competência (folha de setembro até 30/09)</option>
                    <option value="1">do mês seguinte (folha de setembro até 10/10)</option>
                  </select>
                  {!cfg.prazo_dia && <span className="small text-muted">— sem dia, a empresa não entra no controle de prazo</span>}
                </div>

                <hr className="my-3" />
                <div className="small fw-bold text-muted mb-2">
                  Colunas de cadastro da tabela (antes das rubricas)
                </div>
                <div className="d-flex flex-wrap gap-3 mb-1">
                  {Object.entries(catalogoColunas).map(([k, rotulo]) => (
                    <label key={k} className="form-check-label small d-inline-flex align-items-center gap-2"
                      style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0"
                        checked={(cfg.colunas || []).includes(k)}
                        onChange={(e) => setCfg({
                          ...cfg,
                          colunas: e.target.checked
                            ? [...Object.keys(catalogoColunas).filter(
                              (x) => x === k || (cfg.colunas || []).includes(x))]
                            : (cfg.colunas || []).filter((x) => x !== k),
                        })} />
                      {rotulo}
                    </label>
                  ))}
                </div>
                <div className="form-text mb-3">
                  Deixe igual à planilha que o cliente usa — a Setho abre com MAT e SALÁRIO.
                </div>

                <hr className="my-3" />
                <div className="small fw-bold text-muted mb-2">
                  Vínculos que entram na folha
                </div>
                <div className="d-flex flex-wrap gap-3 mb-1">
                  {Object.entries(dados?.vinculos_catalogo || {}).map(([k, rotulo]) => (
                    <label key={k} className="form-check-label small d-inline-flex align-items-center gap-2"
                      style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0"
                        checked={(cfg.vinculos || []).includes(k)}
                        onChange={(e) => setCfg({
                          ...cfg,
                          vinculos: e.target.checked
                            ? [...(cfg.vinculos || []), k]
                            : (cfg.vinculos || []).filter((x) => x !== k),
                        })} />
                      {rotulo}
                    </label>
                  ))}
                </div>
                <div className="form-text mb-3">
                  Vale para a empresa inteira (todas as filiais). Quem estiver desmarcado sai da tabela,
                  do arquivo e do fechamento. Um colaborador específico você tira pela lixeira da linha.
                </div>

                <hr className="my-3" />
                <div className="small fw-bold text-muted mb-2">
                  Como a linha é montada — {arquivo.largura || 43} dígitos, sem separador
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-2">
                    <thead className="table-light">
                      <tr>{(arquivo.campos || []).map((c) => (
                        <th key={c.rotulo} className="text-center small">{c.rotulo}<br />
                          <span className="text-muted fw-normal">{c.digitos} díg.</span></th>
                      ))}</tr>
                    </thead>
                  </table>
                </div>
                {arquivo.exemplo && (
                  <div className="small">
                    <span className="text-muted">Primeira linha deste mês:</span>
                    <code className="d-block mt-1 p-2 bg-light border rounded"
                      style={{ wordBreak: 'break-all' }}>{arquivo.exemplo}</code>
                  </div>
                )}
                {(arquivo.recusados || []).length > 0 && (
                  <div className="alert alert-warning small mt-3 mb-0">
                    <b>Fica fora do arquivo:</b>
                    <ul className="mb-0 mt-1">
                      {arquivo.recusados.slice(0, 8).map((r) => <li key={r}>{r}</li>)}
                      {arquivo.recusados.length > 8 && (
                        <li>e outros {arquivo.recusados.length - 8}…</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-light border" onClick={() => setCfg(null)}>Fechar</button>
                <button className="btn btn-primary" onClick={salvarCfg} disabled={salvandoCfg}>
                  <i className={`fas ${salvandoCfg ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 1280 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-calculator me-2"></i>{detalhe.rubrica.rotulo}
                    {detalhe.rubrica.codigo && (
                      <span className="badge bg-light text-muted border ms-2">rubrica {detalhe.rubrica.codigo}</span>
                    )}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {detalhe.col.nome}
                    {detalhe.col.matricula && ` · matrícula ${detalhe.col.matricula}`}
                    {dados?.mes_rotulo && ` · ${dados.mes_rotulo}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body">
                {detalhe.carregando ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                    <div>Buscando no módulo de origem...</div>
                  </div>
                ) : (
                  <>
                    {detalhe.dados?.aviso && (
                      <div className="alert alert-warning small py-2">
                        <i className="fas fa-triangle-exclamation me-2"></i>{detalhe.dados.aviso}
                      </div>
                    )}
                    {detalhe.dados?.fonte_rotulo && (
                      <div className="small text-muted mb-2">
                        <i className="fas fa-bolt me-1" style={{ color: '#0a6ea1' }}></i>
                        {detalhe.dados.fonte_rotulo}
                      </div>
                    )}
                    {detalhe.dados?.formula && (
                      <div className="rounded p-3 mb-3"
                        style={{ background: '#f8f9fa', border: '1px solid #e9ecef', fontVariantNumeric: 'tabular-nums' }}>
                        <div className="small text-muted text-uppercase" style={{ fontSize: '.7rem', letterSpacing: '.4px' }}>
                          Como o valor é calculado
                        </div>
                        <div className="fs-5 fw-semibold" style={{ color: '#00205b' }}>{detalhe.dados.formula}</div>
                      </div>
                    )}
                    {(detalhe.dados?.linhas || []).length > 0 && (
                      <table className="table table-sm align-middle mb-3">
                        <tbody>
                          {detalhe.dados.linhas.map((l, i) => (
                            <tr key={i} className={l.destaque ? 'table-light' : ''}>
                              <th className="fw-normal small" style={{ width: '60%' }}>{l.rotulo}</th>
                              <td className={`text-end ${l.destaque ? 'fw-bold' : ''}`}
                                style={{ fontVariantNumeric: 'tabular-nums' }}>{l.valor}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {(detalhe.dados?.dias || []).length > 0 && (
                      <>
                        <div className="small fw-bold text-muted mb-1">
                          Espelho do mês ({detalhe.dados.dias.length} dia(s)) — o mesmo do
                          Resumo de Ponto; em destaque, os dias que entram nesta rubrica
                        </div>
                        <table className="table table-sm align-middle">
                          <thead className="table-light">
                            <tr>
                              <th className="small">Dia</th>
                              <th className="small">Batidas</th>
                              <th className="small text-end">Trabalhado</th>
                              <th className="small text-end">Esperado</th>
                              <th className="small">Situação</th>
                              <th className="small text-end">Falta / Sobra</th>
                              <th className="small text-end">Em horas</th>
                              <th className="small text-end">Nesta rubrica</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalhe.dados.dias.map((d, i) => (
                              <tr key={i} style={d.conta ? { background: '#f2f9fc' } : undefined}>
                                <td className="small" style={{ whiteSpace: 'nowrap' }}>
                                  {d.data}
                                  {d.feriado && (
                                    <div style={{ color: '#00205b', fontSize: '.7rem' }}>
                                      <i className="fas fa-star me-1"></i>{d.feriado}
                                    </div>
                                  )}
                                  {d.abono && (
                                    <div style={{ color: '#8a6d00', fontSize: '.7rem' }}>
                                      <i className="fas fa-hourglass-half me-1"></i>
                                      {d.abono === 'folga' ? 'folga' : 'não cobrar'}
                                    </div>
                                  )}
                                </td>
                                <td className="small"
                                  style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {d.batidas}
                                </td>
                                <td className="small text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {d.trabalhado != null ? paraBr(d.trabalhado) : '—'}
                                  {d.trabalhado_hm && (
                                    <div className="text-muted" style={{ fontSize: '.7rem' }}>{d.trabalhado_hm}</div>
                                  )}
                                </td>
                                <td className="small text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {d.esperado != null ? paraBr(d.esperado) : '—'}
                                </td>
                                <td className="small">
                                  <span className="badge rounded-pill"
                                    style={d.situacao === 'OK'
                                      ? { background: '#e8f5e9', color: '#1b5e20', border: '1px solid #2e7d32' }
                                      : d.situacao === 'Não bateu'
                                        ? { background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }
                                        : { background: '#f1f3f5', color: '#495057', border: '1px solid #adb5bd' }}>
                                    {d.situacao}
                                  </span>
                                </td>
                                <td className="small text-end" style={{ fontVariantNumeric: 'tabular-nums',
                                  color: d.saldo > 0 ? '#1b5e20' : d.saldo < 0 ? '#a11d2b' : '#6c757d' }}>
                                  {d.saldo != null ? `${d.saldo > 0 ? '+' : ''}${paraBr(d.saldo)}` : '—'}
                                </td>
                                <td className="small text-end fw-semibold" style={{ whiteSpace: 'nowrap',
                                  color: d.saldo > 0 ? '#1b5e20' : d.saldo < 0 ? '#a11d2b' : '#6c757d' }}>
                                  {d.saldo_hm || '—'}
                                </td>
                                <td className="small text-end fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {d.conta ? paraBr(d.valor) : <span className="text-muted fw-normal">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {detalhe.dados?.manual && (
                      <div className="alert alert-light border small py-2 mb-0">
                        <i className="fas fa-pen me-2 text-muted"></i>
                        Este mês tem <b>lançamento à mão</b> de {paraBr(detalhe.dados.manual.valor)}
                        {detalhe.dados.manual.autor && ` por ${detalhe.dados.manual.autor}`}
                        {detalhe.dados.manual.em && ` em ${detalhe.dados.manual.em}`} — é ele que vai no arquivo.
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                {rotaDaFonte(detalhe.rubrica.fonte, clienteId, mes) && (
                  <Link className="btn btn-outline-primary me-auto"
                    to={rotaDaFonte(detalhe.rubrica.fonte, clienteId, mes)}>
                    <i className="fas fa-arrow-up-right-from-square me-2"></i>
                    Abrir o módulo
                  </Link>
                )}
                <button className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {avisoEntrada && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title fw-bold mb-0" style={{ color: '#212529' }}>
                  <i className="fas fa-triangle-exclamation me-2" style={{ color: '#f1be48' }}></i>
                  Antes de fazer a folha
                </h5>
                <button type="button" className="btn-close" onClick={() => setAvisoEntrada(false)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Para fazer a folha, estes módulos <b>precisam estar feitos antes</b>:
                </p>
                <ul className="list-unstyled mb-3">
                  <li className="mb-2"><i className="fas fa-utensils me-2 text-primary"></i><b>VA</b> (vale-alimentação)</li>
                  <li className="mb-2"><i className="fas fa-bus me-2 text-primary"></i><b>VT</b> (vale-transporte)</li>
                  <li className="mb-2"><i className="fas fa-house-laptop me-2 text-primary"></i><b>Home office</b></li>
                  <li className="mb-2"><i className="fas fa-clock me-2 text-primary"></i><b>Ponto do cliente</b> conferido (batidas fechadas)</li>
                </ul>
                <div className="alert alert-warning small mb-0">
                  <i className="fas fa-link me-2"></i>
                  Os módulos se conversam: a folha puxa os descontos de VA/VT, o home office e
                  as horas extras e faltas do ponto. Se algum não estiver pronto, a folha sai
                  com valores faltando.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={() => setAvisoEntrada(false)}>
                  <i className="fas fa-check me-2"></i>Entendi, já fiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhFolha;
